import Icon8Checkmark from "~/src/components/icons/Icon8checkmark";

export interface FaqItem {
  id: number;
  question: string;
  answer: string | JSX.Element;
}

export interface FaqContent {
  title: string;
  items: FaqItem[];
}

/* INFO ABOUT DATA
Sections and ID ranges covered by these sections:
General, 1-10
Smoke, 11-20
Electrical, 21-40
Gas, 41-60
Caravan Gas Compliance Check, 61-70
Switchboard Upgrade, 71-80
Interconnected Smoke Alarm, 81-90
Property Compliance Service, 91-100
Solutions for Landlords, 101-110
Solutions for Property Managers, 111-120
Solutions for Homeowners, 121-130
Solutions for Building Managers, 131-140
Solutions for Real Estate, 141-150
Legislation Page Elements, 151-160*/

const FaqSectionsContent: FaqContent[] = [
  {
    title: "Najczęstsze pytania ogólne",
    items: [
      {
        id: 1,
        question: "Jak umówić się na przegląd przez platformę?",
        answer: (
          <>
            <p>
              Wypełnij formularz zapytania na inspektomat.pl — trafi ono
              jednocześnie do kilku certyfikowanych firm działających w Twoim
              mieście. Porównujesz oferty, terminy i opinie, a wizytę umawiasz
              bezpośrednio z wybranym wykonawcą. Większość firm w bazie pracuje
              od poniedziałku do piątku w godzinach 8:00–18:00.
            </p>
          </>
        ),
      },
      {
        id: 2,
        question: "Ile czasu trwa wizyta?",
        answer: (
          <>
            <p className="mb-2">Czas trwania przeglądów wygląda następująco:</p>
            <ul className="mb-1 list-disc space-y-2 pl-5">
              <li>Przegląd instalacji elektrycznej: 1–2 godziny</li>
              <li>
                Przegląd instalacji gazowej: 1–3 godziny, w zależności od liczby
                urządzeń
              </li>
              <li>Kontrola czujników dymu: około 15 minut</li>
            </ul>
            Czas trwania może się różnić w zależności od ilości urządzeń lub
            potrzeby wykonania pilnych napraw.
          </>
        ),
      },
      {
        id: 3,
        question: "Jak przygotować się do wizyty specjalisty?",
        answer: (
          <>
            <p className="mb-2">
              Uprawniony specjalista skontaktuje się z Tobą przed wizytą
              i poinformuje o szczegółowych wymaganiach. Ogólna zasada:
            </p>
            <ul className="mb-1 list-disc space-y-2 pl-5">
              <li>
                Przegląd elektryczny: zapewnij dostęp do rozdzielnicy i
                wszystkich gniazdek.
              </li>
              <li>
                Przegląd gazowy: dostęp do kurka gazowego, kotła, kuchenki i
                przewodów.
              </li>
              <li>
                Czujniki dymu: dostęp do każdego czujnika w lokalu.
              </li>
            </ul>
            <p>Obecność lokatora lub właściciela podczas wizyty jest zalecana.</p>
          </>
        ),
      },
      {
        id: 4,
        question: "Jakie są ryzyka związane z brakiem przeglądów instalacji?",
        answer: (
          <>
            <p className="mb-2">
              Brak regularnych przeglądów instalacji elektrycznych, gazowych
              oraz czujników dymu może prowadzić do poważnych zagrożeń dla
              zdrowia i życia mieszkańców, a także do odpowiedzialności prawnej
              właściciela nieruchomości.
            </p>
            <p>
              Zgodnie z Prawem budowlanym przegląd instalacji elektrycznej
              wymagany jest co 5 lat (art. 62 ust. 1 pkt 2), gazowej – co rok
              (art. 62 ust. 1 pkt 1 lit. c), a czujniki dymu należy testować
              co najmniej raz w roku. Każdy przegląd musi być wykonany przez
              uprawnionego specjalistę.
            </p>
          </>
        ),
      },
      {
        id: 5,
        question:
          "Czy inspektomat sam wykonuje przeglądy, czy pomaga porównać oferty firm?",
        answer: (
          <>
            <p className="mb-2">
              Inspektomat działa jako platforma, która pomaga znaleźć i
              porównać oferty firm wykonujących przeglądy i inspekcje.
            </p>
            <p>
              Wypełniasz jedno zgłoszenie, a następnie możesz porównać dostępne
              firmy, terminy i zakres usług bez samodzielnego obdzwaniania
              rynku. Wizytę ustalasz z wybranym wykonawcą.
            </p>
          </>
        ),
      },
      {
        id: 6,
        question: "Jakie usługi mogę zamówić przez platformę?",
        answer: (
          <>
            <p className="mb-2">
              Przez platformę możesz znaleźć wykonawców do przeglądów
              elektrycznych, gazowych, kominowych, wentylacyjnych,
              klimatyzacyjnych, przeciwpożarowych i budowlanych.
            </p>
            <p>
              Obejmuje to między innymi przegląd instalacji elektrycznej,
              pomiary elektryczne, przegląd gazowy, próbę szczelności,
              przegląd kominiarski, serwis klimatyzacji, kontrolę wentylacji,
              systemów wykrywania pożaru, sprzętu PPOŻ oraz przeglądy
              budowlane.
            </p>
          </>
        ),
      },
      {
        id: 7,
        question: "Dla jakich nieruchomości przeznaczona jest platforma?",
        answer: (
          <>
            <p className="mb-2">
              Platforma jest przeznaczona zarówno dla klientów indywidualnych,
              jak i dla osób lub firm zarządzających większą liczbą obiektów.
            </p>
            <p>
              Z usług korzystają właściciele mieszkań i domów, wynajmujący,
              wspólnoty i zarządcy nieruchomości, biura nieruchomości, a także
              firmy odpowiadające za lokale usługowe, biura i budynki firmowe.
            </p>
          </>
        ),
      },
      {
        id: 8,
        question: "Czy mogę zorganizować kilka przeglądów przy jednej obsłudze?",
        answer: (
          <>
            <p className="mb-2">
              W wielu przypadkach tak. Dotyczy to zwłaszcza sytuacji, gdy chcesz
              uporządkować kilka obowiązków dla jednej nieruchomości lub jednego
              budynku.
            </p>
            <p>
              W praktyce wiele firm łączy na przykład przegląd elektryczny,
              gazowy, kominiarski albo kontrolę wentylacji w jednym procesie
              ustalania terminu. Dokładny zakres zależy od lokalizacji,
              dostępności wykonawców i rodzaju potrzebnych kontroli.
            </p>
          </>
        ),
      },
      {
        id: 9,
        question: "Kto wykonuje przegląd i jakie uprawnienia powinien mieć?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd powinien wykonywać specjalista z uprawnieniami
              odpowiednimi do danej branży i rodzaju kontroli.
            </p>
            <p>
              Inne kwalifikacje są potrzebne przy instalacji elektrycznej, inne
              przy gazowej, kominowej czy przeciwpożarowej. Dlatego przy
              wyborze wykonawcy najważniejsze jest to, aby zakres jego
              uprawnień odpowiadał usłudze, którą chcesz zlecić.
            </p>
          </>
        ),
      },
      {
        id: 10,
        question: "Czy po wizycie otrzymam protokół lub dokumentację?",
        answer: (
          <>
            <p className="mb-2">
              Tak, przy większości usług efektem wizyty jest protokół, raport
              albo inna dokumentacja potwierdzająca zakres kontroli i jej wynik.
            </p>
            <p>
              Taki dokument pomaga uporządkować obowiązki właściciela lub
              zarządcy, bywa potrzebny do dokumentacji technicznej budynku,
              ubezpieczenia, najmu, sprzedaży nieruchomości albo dalszych prac
              serwisowych.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - czujniki dymu",
    items: [
      {
        id: 11,
        question:
          "Kto odpowiada za czujniki dymu w wynajmowanej nieruchomości?",
        answer: (
          <>
            <p>
              Zgodnie z obowiązującymi przepisami to właściciel nieruchomości ma
              obowiązek:
            </p>
            <ul className="mb-1 list-disc space-y-2 pl-5">
              <li>Zadbać o prawidłowy montaż i konserwację czujników dymu</li>
              <li>Zapewnić coroczne testowanie każdego czujnika</li>
              <li>Wymieniać baterie w razie potrzeby</li>
            </ul>
            <p>
              Najemca powinien niezwłocznie poinformować właściciela, jeśli
              którykolwiek z czujników przestanie działać.
            </p>
          </>
        ),
      },
      {
        id: 12,
        question:
          "Czy właściciel musi zatrudnić fachowca do konserwacji czujników?",
        answer: (
          <>
            <p className="mb-2">
              Zaleca się, by właściciele zlecali przegląd czujników dymu
              wyspecjalizowanym firmom. Taki wykonawca posiada ubezpieczenie OC
              i gwarantuje profesjonalne wykonanie usługi.
            </p>
          </>
        ),
      },
      {
        id: 13,
        question: "Ile czujników dymu powinno być w domu lub mieszkaniu?",
        answer: (
          <>
            <p>
              Zgodnie z przepisami, co najmniej jeden czujnik powinien być
              zamontowany na każdej kondygnacji budynku. Dokładna liczba zależy
              od układu pomieszczeń.
            </p>
            <p>
              Czujniki powinny spełniać normy bezpieczeństwa zgodne z
              odpowiednimi normami europejskimi.
            </p>
          </>
        ),
      },
      {
        id: 14,
        question: "Czy konserwacja czujników to tylko wymiana baterii?",
        answer: (
          <>
            <p className="mb-2">
              Nie – czujniki mogą tracić skuteczność z powodu kurzu, owadów lub
              starzenia się elektroniki. Dlatego regularna konserwacja i testy
              są niezbędne.
            </p>
            <p>
              Czujniki należy wymieniać co 10 lat lub wcześniej – zgodnie z
              terminem przydatności.
            </p>
          </>
        ),
      },
      {
        id: 15,
        question: "Jak często należy wykonywać przegląd czujników dymu?",
        answer: (
          <>
            <p>
              Zaleca się, aby każdy czujnik dymu był testowany przynajmniej raz
              do roku.
            </p>
          </>
        ),
      },
      {
        id: 16,
        question: "Czy dotyczy to także czujników 240 V zasilanych z sieci?",
        answer: (
          <>
            <p>
              Tak. Czujniki zasilane z sieci 230 V również wymagają przeglądów
              – należy regularnie testować ich działanie, czyścić je, wymieniać
              baterie zapasowe oraz wymieniać urządzenia po upływie ich
              ważności.
            </p>
          </>
        ),
      },
      {
        id: 17,
        question: "Jakie czujniki są montowane podczas wymiany?",
        answer: (
          <>
            <p>
              Certyfikowane firmy z naszej bazy montują czujniki dymu z
              technologią fotoelektryczną – są one standardem w budynkach
              mieszkalnych i cechują się wysoką czułością.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - bezpieczeństwo elektryczne",
    items: [
      {
        id: 21,
        question:
          "Czym jest przegląd elektryczny i jak często trzeba go wykonywać?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd elektryczny to kontrola wszystkich elementów instalacji,
              gniazdek, oświetlenia i zabezpieczeń, wykonywana zgodnie z
              obowiązującymi normami.
            </p>
            <p>
              Zgodnie z art. 62 ust. 1 pkt 2 Prawa budowlanego przegląd
              instalacji elektrycznej wymagany jest co 5 lat, a w przypadku
              wynajmu zaleca się go również przed każdą zmianą najemcy.
              Przegląd musi być przeprowadzony przez licencjonowanego
              elektryka.
            </p>
          </>
        ),
      },
      {
        id: 22,
        question:
          "Kto odpowiada za wykonanie przeglądu elektrycznego w wynajmowanym mieszkaniu?",
        answer: (
          <>
            <p className="mb-2">
              Odpowiedzialność za regularny przegląd instalacji elektrycznej
              spoczywa na właścicielu nieruchomości.
            </p>
            <p>
              Na żądanie najemcy właściciel powinien przekazać pisemną
              informację z datą ostatniego przeglądu.
            </p>
          </>
        ),
      },
      {
        id: 23,
        question: "Kto jest uprawniony do wykonania przeglądu elektrycznego?",
        answer: (
          <>
            Zgodnie z prawem, przegląd instalacji elektrycznej może wykonać
            wyłącznie licencjonowany lub zarejestrowany elektryk.
          </>
        ),
      },
      {
        id: 24,
        question: "Do jakich pomieszczeń będzie potrzebował dostępu elektryk?",
        answer: (
          <>
            Przegląd instalacji elektrycznej obejmuje kontrolę wszystkich
            dostępnych elementów instalacji, osprzętu i urządzeń. Elektryk musi
            mieć dostęp do gniazdek, włączników, rozdzielnicy elektrycznej oraz
            urządzeń elektrycznych stanowiących wyposażenie wynajmowanego
            lokalu.
          </>
        ),
      },
      {
        id: 25,
        question: "Po co wykonywać przegląd instalacji elektrycznej?",
        answer: (
          <>
            <p>
              Przegląd pozwala wykryć potencjalne zagrożenia i upewnić się, że
              instalacja działa bezpiecznie, chroniąc zarówno nieruchomość, jak
              i jej mieszkańców.
            </p>
          </>
        ),
      },
      {
        id: 26,
        question: "Jak często należy wykonywać przegląd elektryczny?",
        answer: (
          <>
            <p>
              Zgodnie z Prawem budowlanym (art. 62 ust. 1 pkt 2) właściciele
              nieruchomości mają obowiązek zapewnić wykonanie przeglądu
              instalacji elektrycznej co 5 lat przez uprawnionego elektryka.
            </p>
          </>
        ),
      },
      {
        id: 27,
        question: "Na czym polega przegląd elektryczny?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd instalacji elektrycznej to szczegółowa kontrola systemu
              elektrycznego w budynku, w tym okablowania, rozdzielnic i urządzeń
              elektrycznych.
            </p>
            <p>
              Po przeglądzie właściciel otrzymuje szczegółowy raport z wynikami
              oraz, jeśli to konieczne, wycenę napraw lub modernizacji.
            </p>
          </>
        ),
      },
      {
        id: 28,
        question: "Czy mogę samodzielnie zrobić przegląd elektryczny?",
        answer: (
          <>
            <p>
              Nie. Przegląd elektryczny może wykonać wyłącznie licencjonowany
              specjalista z odpowiednimi uprawnieniami.
            </p>
          </>
        ),
      },
      {
        id: 29,
        question: "Ile czasu trwa przegląd elektryczny?",
        answer: (
          <>
            <p>
              Standardowy przegląd elektryczny trwa od jednej do trzech godzin.
              Czas ten zależy od wielkości nieruchomości i złożoności
              instalacji.
            </p>
          </>
        ),
      },
      {
        id: 30,
        question:
          "Co się stanie, jeśli instalacja elektryczna nie przejdzie przeglądu?",
        answer: (
          <>
            <p>
              Jeśli instalacja nie przejdzie przeglądu, elektryk sporządzi
              szczegółowy raport z wykrytymi nieprawidłowościami oraz
              zaleceniami dotyczącymi napraw lub modernizacji. Może również
              przygotować wycenę tych prac.
            </p>
          </>
        ),
      },
      {
        id: 31,
        question: "Jakie są objawy, że potrzebny jest przegląd elektryczny?",
        answer: (
          <>
            <p>Na konieczność wykonania przeglądu mogą wskazywać:</p>
            <ul className="mb-1 list-disc space-y-1 pl-4">
              <li>Częste zaniki zasilania</li>
              <li>
                Wyłączające się bezpieczniki lub wyzwalacze różnicowoprądowe
              </li>
              <li>Mrugające światła</li>
              <li>Przebarwione lub nadpalone gniazdka</li>
              <li>Zapach spalenizny</li>
              <li>Brzęczenie w instalacji</li>
            </ul>
            <p>
              Jeśli zauważysz którykolwiek z tych objawów, umów przegląd jak
              najszybciej.
            </p>
          </>
        ),
      },
      {
        id: 32,
        question: "Jak bardzo przegląd elektryczny zakłóci mój dzień?",
        answer: (
          <>
            <p>
              Przegląd może trwać kilka godzin, a w tym czasie może być
              konieczne wyłączenie zasilania w niektórych częściach budynku.
              Uprawniony elektryk w miarę możliwości ogranicza niedogodności
              do minimum.
            </p>
          </>
        ),
      },
      {
        id: 33,
        question: "Czy muszę być obecny podczas przeglądu elektrycznego?",
        answer: (
          <>
            <p>
              Nie jest to obowiązkowe, ale zalecane. Obecność właściciela lub
              najemcy pozwala na bezpośrednią rozmowę z elektrykiem, zadanie
              pytań i omówienie ewentualnych usterek lub sposobów ich usunięcia.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - pomiary elektryczne",
    items: [
      {
        id: 161,
        question: "Czy pomiary elektryczne są obowiązkowe?",
        answer: (
          <>
            <p className="mb-2">
              Tak, w wielu przypadkach są obowiązkowe. Prawo budowlane wymaga,
              aby co najmniej raz na 5 lat przeprowadzać kontrolę obejmującą
              również badanie instalacji elektrycznej i piorunochronnej.
            </p>
            <p>
              Pomiary wykonuje się także przy odbiorze nowej instalacji, po
              rozbudowie, modernizacji lub po awarii, gdy trzeba potwierdzić
              bezpieczeństwo dalszego użytkowania.
            </p>
          </>
        ),
      },
      {
        id: 162,
        question:
          "Co ile należy wykonywać pomiary instalacji elektrycznej?",
        answer: (
          <>
            <p className="mb-2">
              W ramach kontroli okresowej standardowy termin to co najmniej raz
              na 5 lat.
            </p>
            <p>
              W praktyce pomiary wykonuje się także częściej, jeśli instalacja
              pracuje w trudnych warunkach, była modernizowana, wystąpiła awaria
              albo obiekt wymaga pomiarów odbiorczych przed uruchomieniem.
            </p>
          </>
        ),
      },
      {
        id: 163,
        question: "Na czym polegają pomiary elektryczne instalacji?",
        answer: (
          <>
            <p className="mb-2">
              Pomiary elektryczne polegają na sprawdzeniu, czy instalacja jest
              bezpieczna i czy działa zgodnie z wymaganiami technicznymi.
            </p>
            <p>
              Obejmują one badanie między innymi stanu izolacji przewodów,
              skuteczności ochrony przeciwporażeniowej, uziemienia, ciągłości
              połączeń ochronnych, działania zabezpieczeń oraz wyłączników
              różnicowoprądowych.
            </p>
          </>
        ),
      },
      {
        id: 164,
        question: "Jakie pomiary wykonuje się w instalacji elektrycznej?",
        answer: (
          <>
            <p className="mb-2">
              Zakres zależy od obiektu i celu badania, ale najczęściej wykonuje
              się:
            </p>
            <ul className="list-disc space-y-2 pl-5">
              <li>pomiar rezystancji izolacji przewodów,</li>
              <li>pomiar uziemienia,</li>
              <li>sprawdzenie ciągłości przewodów ochronnych i połączeń,</li>
              <li>pomiar impedancji pętli zwarcia,</li>
              <li>testy wyłączników różnicowoprądowych,</li>
              <li>pomiary instalacji odgromowej.</li>
            </ul>
          </>
        ),
      },
      {
        id: 165,
        question:
          "Czy pomiary elektryczne wykonuje się w domu jednorodzinnym?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Dom jednorodzinny również powinien przechodzić pomiary
              instalacji elektrycznej w ramach kontroli pięcioletniej.
            </p>
            <p>
              Wyłączenia przewidziane w Prawie budowlanym dotyczą części
              kontroli rocznych, ale nie zwalniają z okresowego badania
              instalacji elektrycznej i piorunochronnej co najmniej raz na 5
              lat.
            </p>
          </>
        ),
      },
      {
        id: 166,
        question:
          "Czy pomiary elektryczne wykonuje się w mieszkaniu w bloku?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Pomiary wykonuje się zarówno w mieszkaniach, jak i w
              częściach wspólnych budynku.
            </p>
            <p>
              W praktyce zakres odpowiedzialności bywa podzielony: właściciel
              lokalu odpowiada za instalację w mieszkaniu, a zarządca lub
              wspólnota za części wspólne, piony, rozdzielnie i pozostałą
              infrastrukturę budynku.
            </p>
          </>
        ),
      },
      {
        id: 167,
        question: "Kiedy wykonuje się pomiary elektryczne instalacji?",
        answer: (
          <>
            <p className="mb-2">Pomiary wykonuje się najczęściej:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>przy odbiorze nowej instalacji, domu lub lokalu,</li>
              <li>po remoncie, rozbudowie albo wymianie rozdzielnicy,</li>
              <li>po awarii, zalaniu, przepięciu lub pożarze,</li>
              <li>w ramach kontroli okresowej co najmniej raz na 5 lat,</li>
              <li>przed wynajmem, sprzedażą lub zmianą sposobu użytkowania.</li>
            </ul>
          </>
        ),
      },
      {
        id: 168,
        question:
          "Czy pomiary elektryczne są wymagane przy odbiorze domu lub instalacji?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Przy odbiorze nowej lub przebudowanej instalacji wykonuje się
              pomiary odbiorcze, które potwierdzają, że instalacja może być
              bezpiecznie użytkowana.
            </p>
            <p>
              Bez tych badań trudno rzetelnie potwierdzić poprawność wykonania
              instalacji i przygotować kompletną dokumentację powykonawczą.
            </p>
          </>
        ),
      },
      {
        id: 169,
        question: "Ile kosztują pomiary elektryczne instalacji?",
        answer: (
          <>
            <p className="mb-2">
              Koszt pomiarów ustala się indywidualnie. Małe mieszkanie z prostą
              instalacją będzie wycenione inaczej niż dom jednorodzinny, lokal
              usługowy czy budynek z rozbudowaną rozdzielnią.
            </p>
            <p>
              Najlepszą praktyką jest wycena po określeniu rodzaju obiektu,
              liczby obwodów i zakresu dokumentacji, którą klient ma otrzymać po
              badaniu.
            </p>
          </>
        ),
      },
      {
        id: 170,
        question: "Od czego zależy cena pomiarów elektrycznych?",
        answer: (
          <>
            <p className="mb-2">Na cenę wpływa przede wszystkim:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>wielkość i typ obiektu,</li>
              <li>liczba obwodów, rozdzielnic, punktów i zabezpieczeń,</li>
              <li>czy badanie obejmuje też uziemienie lub odgromówkę,</li>
              <li>stopień dostępności instalacji i termin realizacji,</li>
              <li>zakres protokołu oraz ewentualnych zaleceń naprawczych.</li>
            </ul>
          </>
        ),
      },
      {
        id: 171,
        question:
          "Czy po wykonaniu pomiarów elektrycznych otrzymuje się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Po wykonaniu pomiarów standardem jest protokół zawierający
              zakres badania, wyniki pomiarów, ocenę stanu instalacji oraz
              wnioski końcowe.
            </p>
            <p>
              Taki dokument jest potrzebny między innymi przy odbiorach,
              kontroli okresowej, dla zarządcy obiektu, ubezpieczyciela albo do
              planowania napraw i modernizacji.
            </p>
          </>
        ),
      },
      {
        id: 172,
        question:
          "Kto może wykonywać pomiary elektryczne instalacji?",
        answer: (
          <>
            <p className="mb-2">
              Pomiary powinny wykonywać osoby posiadające odpowiednie
              kwalifikacje do pracy przy urządzeniach i instalacjach
              elektrycznych.
            </p>
            <p>
              W przypadku kontroli okresowych wymaganych przez Prawo budowlane
              badanie powinno być wykonane przez osobę posiadającą kwalifikacje
              dopuszczone przez przepisy dla kontroli instalacji elektrycznych i
              piorunochronnych.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd instalacji odgromowej",
    items: [
      {
        id: 181,
        question: "Czy przegląd instalacji odgromowej jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Instalacja odgromowa jest objęta obowiązkową kontrolą
              okresową, która zgodnie z Prawem budowlanym powinna być wykonana
              co najmniej raz na 5 lat.
            </p>
            <p>
              Obowiązek dotyczy badania stanu technicznego i przydatności do
              użytkowania instalacji piorunochronnej, a za jego realizację
              odpowiada właściciel lub zarządca obiektu.
            </p>
          </>
        ),
      },
      {
        id: 182,
        question:
          "Kiedy poza kontrolą 5-letnią warto wykonać przegląd instalacji odgromowej?",
        answer: (
          <>
            <p className="mb-2">
              Dodatkowy przegląd warto zlecić po wyładowaniu atmosferycznym, po
              remoncie dachu, elewacji lub ociepleniu, a także po montażu
              fotowoltaiki, klimatyzatorów lub innych urządzeń na dachu.
            </p>
            <p>
              Kontrolę warto zrobić również wtedy, gdy widać korozję,
              poluzowane mocowania, zerwane przewody albo nie ma pewności, kiedy
              wykonano ostatnie pomiary.
            </p>
          </>
        ),
      },
      {
        id: 183,
        question: "Na czym polega przegląd instalacji odgromowej?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd polega na ocenie, czy cały system piorunochronny nadal
              może skutecznie przejąć energię wyładowania i bezpiecznie
              odprowadzić ją do ziemi.
            </p>
            <p>
              W praktyce obejmuje oględziny elementów instalacji oraz pomiary,
              które potwierdzają ciągłość połączeń i stan układu uziemiającego.
            </p>
          </>
        ),
      },
      {
        id: 184,
        question: "Co obejmuje kontrola instalacji odgromowej?",
        answer: (
          <>
            <p className="mb-2">Typowa kontrola obejmuje sprawdzenie:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>zwodów i przewodów odprowadzających,</li>
              <li>złączy kontrolnych i połączeń,</li>
              <li>stanu mocowań oraz śladów korozji,</li>
              <li>uziomu i dostępnych elementów układu ochronnego,</li>
              <li>uszkodzeń po wcześniejszych pracach dachowych lub elewacyjnych.</li>
            </ul>
          </>
        ),
      },
      {
        id: 185,
        question: "Jakie pomiary wykonuje się przy instalacji odgromowej?",
        answer: (
          <>
            <p className="mb-2">
              Zakres zależy od obiektu, ale najczęściej wykonuje się pomiary
              związane z uziemieniem oraz sprawdzenie ciągłości połączeń między
              elementami instalacji.
            </p>
            <p>
              Same pomiary powinny być interpretowane razem z oględzinami, bo
              poprawny wynik liczbowy nie wyklucza uszkodzeń mechanicznych albo
              korozji instalacji.
            </p>
          </>
        ),
      },
      {
        id: 186,
        question: "Czy dom jednorodzinny też wymaga przeglądu odgromowego?",
        answer: (
          <>
            <p className="mb-2">
              Tak, jeśli budynek ma instalację odgromową, jej stan również
              powinien być objęty kontrolą okresową.
            </p>
            <p>
              Dotyczy to domów jednorodzinnych tak samo jak budynków
              wielorodzinnych, obiektów firmowych czy przemysłowych.
            </p>
          </>
        ),
      },
      {
        id: 187,
        question: "Kto może wykonać przegląd instalacji odgromowej?",
        answer: (
          <>
            <p className="mb-2">
              Kontrolę i pomiary powinny wykonywać osoby posiadające właściwe
              kwalifikacje do oceny instalacji elektrycznych i
              piorunochronnych.
            </p>
            <p>
              Przy kontrolach okresowych ważne jest nie tylko wykonanie samego
              pomiaru, ale też uprawniona ocena stanu technicznego i poprawne
              sporządzenie dokumentacji.
            </p>
          </>
        ),
      },
      {
        id: 188,
        question:
          "Czy po przeglądzie instalacji odgromowej otrzymuje się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Po kontroli właściciel lub zarządca powinien otrzymać
              protokół z oględzin, wyników pomiarów i oceną stanu instalacji.
            </p>
            <p>
              Dokument jest potrzebny do prowadzenia dokumentacji technicznej
              budynku, przy kontrolach oraz przy planowaniu napraw lub
              modernizacji systemu ochrony odgromowej.
            </p>
          </>
        ),
      },
      {
        id: 189,
        question: "Od czego zależy cena przeglądu instalacji odgromowej?",
        answer: (
          <>
            <p className="mb-2">Na cenę wpływa przede wszystkim:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>wielkość i wysokość budynku,</li>
              <li>liczba przewodów odprowadzających i punktów pomiarowych,</li>
              <li>dostępność dachu, uziomu i złączy kontrolnych,</li>
              <li>zakres wymaganych pomiarów oraz dokumentacji,</li>
              <li>to, czy kontrola jest standardowa, czy wykonywana po zdarzeniu lub remoncie.</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - rozdzielnia elektryczna",
    items: [
      {
        id: 191,
        question: "Czym jest rozdzielnia elektryczna?",
        answer: (
          <>
            <p className="mb-2">
              Rozdzielnia elektryczna to punkt, w którym energia jest
              rozdzielana na poszczególne obwody budynku i gdzie znajdują się
              najważniejsze zabezpieczenia instalacji.
            </p>
            <p>
              To od jej stanu zależy między innymi czytelność układu, wygoda
              serwisu i bezpieczeństwo codziennego użytkowania całej instalacji.
            </p>
          </>
        ),
      },
      {
        id: 192,
        question:
          "Kiedy wystarczy kontrola rozdzielni, a kiedy potrzebna jest modernizacja?",
        answer: (
          <>
            <p className="mb-2">
              Sama kontrola ma sens wtedy, gdy chcesz najpierw ocenić stan
              rozdzielni i ustalić, czy problem wymaga większej ingerencji.
            </p>
            <p>
              Modernizacja jest uzasadniona wtedy, gdy układ działa, ale wymaga
              poprawy zabezpieczeń, uporządkowania obwodów, wymiany części
              aparatury albo przygotowania rozdzielni na nowe obciążenia.
            </p>
          </>
        ),
      },
      {
        id: 193,
        question:
          "Jakie objawy wskazują, że rozdzielnia elektryczna wymaga pilnej uwagi?",
        answer: (
          <>
            <p className="mb-2">Najczęstsze sygnały ostrzegawcze to:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>częste wyłączanie zabezpieczeń bez jasnej przyczyny,</li>
              <li>zapach nagrzanej izolacji lub ślady przegrzewania,</li>
              <li>buczenie aparatury albo nagrzewająca się obudowa,</li>
              <li>ślady prowizorycznych przeróbek i nieczytelny podział obwodów,</li>
              <li>brak miejsca na nowe zabezpieczenia lub rozbudowę instalacji.</li>
            </ul>
          </>
        ),
      },
      {
        id: 194,
        question: "Kiedy trzeba wymienić rozdzielnię elektryczną?",
        answer: (
          <>
            <p className="mb-2">
              Wymiana jest zwykle potrzebna wtedy, gdy obecna rozdzielnia jest
              zbyt mała, przegrzana, uszkodzona mechanicznie albo skala
              wcześniejszych przeróbek nie pozwala już na bezpieczną
              modernizację.
            </p>
            <p>
              Często dotyczy to starych tablic bez odpowiedniej aparatury
              ochronnej, rozdzielni bez miejsca na dalszą rozbudowę albo
              układów, które nie są przygotowane na obecne obciążenia budynku.
            </p>
          </>
        ),
      },
      {
        id: 199,
        question: "Czy stara rozdzielnia elektryczna jest niebezpieczna?",
        answer: (
          <>
            <p className="mb-2">
              Może być niebezpieczna, jeśli ma przegrzewające się połączenia,
              zużytą aparaturę, brak odpowiednich zabezpieczeń albo była przez
              lata przerabiana prowizorycznie.
            </p>
            <p>
              Sama metryka rozdzielni nie przesądza jeszcze o jej stanie, ale
              im starszy układ i im mniej dokumentacji po wcześniejszych
              zmianach, tym większe ryzyko, że potrzebna będzie kontrola,
              modernizacja albo pełna wymiana.
            </p>
          </>
        ),
      },
      {
        id: 195,
        question: "Jakie zabezpieczenia powinny być w rozdzielni elektrycznej?",
        answer: (
          <>
            <p className="mb-2">
              Zależy to od rodzaju instalacji, ale typowa rozdzielnia obejmuje
              zabezpieczenia nadprądowe, wyłączniki różnicowoprądowe oraz
              rozłącznik główny.
            </p>
            <p>
              W wielu obiektach dochodzą też ograniczniki przepięć i inne
              elementy wynikające z układu zasilania oraz sposobu użytkowania
              budynku.
            </p>
          </>
        ),
      },
      {
        id: 196,
        question:
          "Czy po modernizacji lub wymianie rozdzielni wykonuje się pomiary?",
        answer: (
          <>
            <p className="mb-2">
              Tak, po zakończeniu prac rozdzielnia powinna zostać sprawdzona,
              aby potwierdzić poprawność wykonania i bezpieczeństwo dalszej
              eksploatacji.
            </p>
            <p>
              Zakres badań zależy od rodzaju prac, ale pomiary i kontrola
              końcowa są ważnym elementem odbioru rozdzielni po modernizacji lub
              wymianie.
            </p>
          </>
        ),
      },
      {
        id: 197,
        question:
          "Kto może wykonać modernizację albo wymianę rozdzielni elektrycznej?",
        answer: (
          <>
            <p className="mb-2">
              Prace przy rozdzielni powinny wykonywać osoby posiadające
              odpowiednie kwalifikacje do pracy przy instalacjach elektrycznych.
            </p>
            <p>
              To ważne nie tylko ze względu na bezpieczeństwo samego montażu,
              ale też na późniejszą ocenę poprawności wykonania i dokumentację
              po zakończeniu prac.
            </p>
          </>
        ),
      },
      {
        id: 198,
        question: "Ile kosztuje modernizacja rozdzielni elektrycznej?",
        answer: (
          <>
            <p className="mb-2">
              Koszt modernizacji ustala się indywidualnie. Inaczej wycenia się
              prostą modernizację małej rozdzielni w mieszkaniu, a inaczej
              przebudowę większego układu w domu lub lokalu usługowym.
            </p>
            <p>
              Na cenę wpływa liczba obwodów i faz, stan obecnej aparatury,
              zakres prac przy okablowaniu, dobór zabezpieczeń oraz to, czy po
              zakończeniu przewidziane są pomiary i dokumentacja odbiorcza.
            </p>
          </>
        ),
      },
      {
        id: 200,
        question: "Czy można wymienić rozdzielnię elektryczną w mieszkaniu?",
        answer: (
          <>
            <p className="mb-2">
              Tak, rozdzielnię elektryczną można wymienić również w mieszkaniu,
              jeśli jej stan techniczny jest zły albo układ nie odpowiada
              obecnym potrzebom instalacji.
            </p>
            <p>
              W praktyce często robi się to przy generalnym remoncie, po
              negatywnej ocenie starej tablicy lub wtedy, gdy trzeba dołożyć
              nowe zabezpieczenia i uporządkować podział obwodów w lokalu.
            </p>
          </>
        ),
      },
      {
        id: 201,
        question:
          "Od czego zależy cena modernizacji lub wymiany rozdzielni elektrycznej?",
        answer: (
          <>
            <p className="mb-2">Na cenę wpływa przede wszystkim:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>liczba obwodów i faz,</li>
              <li>stan obecnej rozdzielni i aparatury,</li>
              <li>zakres prac przy okablowaniu i zabezpieczeniach,</li>
              <li>potrzeba przygotowania układu pod nowe odbiory mocy,</li>
              <li>zakres pomiarów końcowych i dokumentacji po wykonaniu prac.</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd instalacji fotowoltaicznej",
    items: [
      {
        id: 211,
        question:
          "Czy przegląd instalacji fotowoltaicznej jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Instalacja fotowoltaiczna jest częścią instalacji elektrycznej
              budynku, dlatego jej stan powinien być uwzględniany w obowiązkowej
              kontroli instalacji elektrycznej wykonywanej co najmniej raz na 5
              lat.
            </p>
            <p>
              Niezależnie od obowiązku prawnego, właściciele instalacji PV często
              wykonują też dodatkowe kontrole wcześniej, zwłaszcza po spadku
              uzysków, po burzy, po rozbudowie systemu albo przed końcem
              gwarancji.
            </p>
          </>
        ),
      },
      {
        id: 212,
        question:
          "Jak często warto wykonywać przegląd instalacji fotowoltaicznej?",
        answer: (
          <>
            <p className="mb-2">
              W praktyce rozsądnie jest kontrolować instalację PV regularnie,
              zwykle co 1-2 lata, a pełną kontrolę elektryczną traktować jako
              minimum w ustawowym cyklu 5-letnim dla instalacji elektrycznej.
            </p>
            <p>
              Dodatkowy przegląd warto zlecić po gradobiciu, silnej burzy,
              zauważalnym spadku produkcji, błędach falownika albo po rozbudowie
              instalacji o nowe komponenty.
            </p>
          </>
        ),
      },
      {
        id: 213,
        question: "Co obejmuje kontrola paneli fotowoltaicznych?",
        answer: (
          <>
            <p className="mb-2">
              Kontrola paneli obejmuje ocenę modułów, mocowań, przewodów,
              złączy i widocznych oznak uszkodzeń mechanicznych, zabrudzeń albo
              przegrzewania.
            </p>
            <p>
              Celem jest sprawdzenie, czy moduły są bezpieczne w eksploatacji i
              czy nie ma usterek, które z czasem mogą obniżyć uzysk lub
              doprowadzić do poważniejszego problemu technicznego.
            </p>
          </>
        ),
      },
      {
        id: 214,
        question: "Czy falownik też wymaga regularnej kontroli?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Falownik pracuje stale i jest jednym z najważniejszych
              elementów całego systemu, dlatego jego stan ma bezpośredni wpływ
              na wydajność i bezpieczeństwo instalacji PV.
            </p>
            <p>
              Podczas kontroli sprawdza się między innymi historię błędów,
              warunki chłodzenia, stan połączeń i podstawowe parametry pracy
              urządzenia.
            </p>
          </>
        ),
      },
      {
        id: 215,
        question: "Jakie pomiary wykonuje się w instalacji fotowoltaicznej?",
        answer: (
          <>
            <p className="mb-2">
              Zakres zależy od obiektu i celu wizyty, ale przegląd może
              obejmować pomiary po stronie AC i DC, ocenę zabezpieczeń oraz
              sprawdzenie współpracy instalacji PV z instalacją budynku.
            </p>
            <p>
              Pomiary są potrzebne po to, żeby potwierdzić nie tylko sam fakt
              produkcji energii, ale też poprawność i bezpieczeństwo pracy
              całego układu.
            </p>
          </>
        ),
      },
      {
        id: 216,
        question: "Czy magazyn energii wymaga osobnej kontroli w instalacji PV?",
        answer: (
          <>
            <p className="mb-2">
              Tak, jeśli magazyn energii współpracuje z fotowoltaiką, jego stan
              i integracja z resztą systemu powinny być oceniane osobno, a nie
              tylko przy okazji oględzin paneli.
            </p>
            <p>
              Szczególnie ważne jest sprawdzenie połączeń, zabezpieczeń,
              komunikacji z falownikiem i warunków pracy systemu BMS.
            </p>
          </>
        ),
      },
      {
        id: 217,
        question: "Kto może wykonać przegląd instalacji fotowoltaicznej?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd powinien wykonywać specjalista mający odpowiednie
              kwalifikacje do pracy przy instalacjach elektrycznych oraz
              doświadczenie w systemach PV.
            </p>
            <p>
              To ważne, bo instalacja fotowoltaiczna obejmuje zarówno stronę AC,
              jak i DC, a ocena jej stanu wymaga praktycznej znajomości pracy
              paneli, falownika, zabezpieczeń i integracji z budynkiem.
            </p>
          </>
        ),
      },
      {
        id: 218,
        question: "Ile kosztuje przegląd instalacji fotowoltaicznej?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od wielkości instalacji, dostępu do paneli, zakresu
              pomiarów, rodzaju falownika oraz tego, czy system współpracuje z
              magazynem energii.
            </p>
            <p>
              Przy wycenie warto sprawdzić, czy oferta obejmuje wyłącznie
              oględziny, czy również pomiary, ocenę falownika i raport z
              wnioskami po kontroli.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd magazynu energii",
    items: [
      {
        id: 231,
        question: "Na czym polega przegląd magazynu energii?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd polega na ocenie stanu połączeń, zabezpieczeń,
              elektroniki sterującej, komunikacji z falownikiem i warunków pracy
              całego systemu bateryjnego.
            </p>
            <p>
              Celem jest sprawdzenie, czy magazyn energii pracuje stabilnie,
              bezpiecznie i zgodnie z założeniami producenta oraz reszty
              instalacji.
            </p>
          </>
        ),
      },
      {
        id: 232,
        question: "Kiedy warto wykonać przegląd magazynu energii?",
        answer: (
          <>
            <p className="mb-2">
              Kontrolę warto wykonać po uruchomieniu systemu, po rozbudowie
              instalacji PV, po zmianie falownika, przed końcem gwarancji albo
              wtedy, gdy pojawiają się alarmy i niepokojące objawy.
            </p>
            <p>
              Dodatkowy przegląd ma sens również po burzy, przepięciu, dłuższym
              postoju systemu albo po zauważalnym spadku pojemności magazynu.
            </p>
          </>
        ),
      },
      {
        id: 233,
        question: "Co obejmuje kontrola magazynu energii?",
        answer: (
          <>
            <p className="mb-2">
              Typowa kontrola obejmuje ocenę połączeń elektrycznych, aparatury
              zabezpieczającej, komunikacji z falownikiem, historii alarmów,
              warunków chłodzenia i podstawowych parametrów pracy systemu.
            </p>
            <p>
              W zależności od rozwiązania sprawdza się też współpracę magazynu z
              instalacją budynku i to, czy system nie wymaga dalszej diagnostyki
              lub korekty konfiguracji.
            </p>
          </>
        ),
      },
      {
        id: 234,
        question:
          "Czy magazyn energii powinien być sprawdzany razem z instalacją fotowoltaiczną?",
        answer: (
          <>
            <p className="mb-2">
              Tak, jeśli magazyn współpracuje z instalacją PV, jego stan warto
              oceniać razem z falownikiem, zabezpieczeniami i logiką pracy
              całego układu.
            </p>
            <p>
              Problem nie zawsze leży w samym magazynie. Część usterek wynika z
              integracji z fotowoltaiką, falownikiem albo rozdzielnią.
            </p>
          </>
        ),
      },
      {
        id: 235,
        question: "Jakie są najczęstsze problemy z magazynem energii?",
        answer: (
          <>
            <p className="mb-2">Najczęściej pojawiają się:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>alarmy systemowe i błędy komunikacji,</li>
              <li>nietypowe nagrzewanie urządzenia,</li>
              <li>spadek pojemności lub wydajności ładowania,</li>
              <li>przerwy w ładowaniu lub rozładowaniu,</li>
              <li>nieprawidłowe przełączanie trybów pracy.</li>
            </ul>
          </>
        ),
      },
      {
        id: 236,
        question: "Czy po przeglądzie magazynu energii otrzymuje się raport?",
        answer: (
          <>
            <p className="mb-2">
              Tak, po kontroli właściciel powinien otrzymać podsumowanie stanu
              systemu i informację, czy magazyn pracuje prawidłowo.
            </p>
            <p>
              W praktyce raport lub protokół pomaga uporządkować dokumentację,
              zaplanować dalszy serwis i zdecydować, czy potrzebna jest
              interwencja producenta albo instalatora.
            </p>
          </>
        ),
      },
      {
        id: 237,
        question: "Kto może wykonać przegląd magazynu energii?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd powinien wykonywać specjalista mający odpowiednie
              kwalifikacje do pracy przy instalacjach elektrycznych i
              doświadczenie w systemach magazynowania energii.
            </p>
            <p>
              To ważne, bo kontrola dotyczy nie tylko samej baterii, ale też
              integracji z falownikiem, PV, zabezpieczeniami i instalacją
              budynku.
            </p>
          </>
        ),
      },
      {
        id: 238,
        question: "Ile kosztuje przegląd magazynu energii?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od typu systemu, jego mocy, zakresu kontroli, stopnia
              integracji z falownikiem i fotowoltaiką oraz tego, czy potrzebne
              są dodatkowe pomiary i analiza alarmów.
            </p>
            <p>
              Przy wycenie warto sprawdzić, czy oferta obejmuje wyłącznie wizytę
              technika, czy również ocenę konfiguracji, raport i wnioski po
              przeglądzie.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - Bezpieczeństwo Gazowe",
    items: [
      {
        id: 41,
        question:
          "Czym jest przegląd instalacji gazowej i jak często należy go wykonywać?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd instalacji gazowej dotyczy nieruchomości z urządzeniami,
              instalacjami lub przyłączami gazowymi.
            </p>
            <p className="mb-2">
              Zgodnie z polskim prawem budowlanym i przepisami BHP zaleca się,
              aby taki przegląd był wykonywany co najmniej raz w roku przez
              osobę posiadającą odpowiednie uprawnienia gazowe.
            </p>
            <p className="mb-2">
              Regularne kontrole zapewniają bezpieczeństwo użytkowników i
              zgodność z obowiązującymi przepisami.
            </p>
          </>
        ),
      },
      {
        id: 42,
        question:
          "Kto jest odpowiedzialny za wykonywanie przeglądów gazowych w wynajmowanej nieruchomości?",
        answer: (
          <>
            <p className="mb-2">
              W Polsce obowiązek zapewnienia regularnych przeglądów instalacji
              gazowej spoczywa na właścicielu lub zarządcy nieruchomości.
            </p>
            <p>
              Wynajmujący musi zapewnić, aby wszystkie urządzenia i instalacje
              gazowe w lokalu były sprawdzane przez uprawnionego specjalistę co
              najmniej raz w roku.
            </p>
          </>
        ),
      },
      {
        id: 43,
        question: "Kto jest uprawniony do przeprowadzania przeglądu gazowego?",
        answer: (
          <>
            Przegląd instalacji gazowej może przeprowadzić wyłącznie osoba
            posiadająca odpowiednie uprawnienia gazowe (SEP grupa 3) oraz
            doświadczenie w wykonywaniu tego typu kontroli.
          </>
        ),
      },
      {
        id: 44,
        question: "Na czym polega przegląd instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">Podczas przeglądu uprawniony specjalista:</p>
            <ul className="mb-2 list-disc space-y-2 pl-5">
              <li>Kontroluje stan wszystkich urządzeń gazowych i przewodów.</li>
              <li>
                Sprawdza szczelność instalacji pod kątem ewentualnych wycieków
                gazu.
              </li>
              <li>
                Weryfikuje poprawność działania wentylacji i przewodów
                spalinowych.
              </li>
            </ul>
            <p className="mb-2">
              Dodatkowo może przeprowadzić testy, aby upewnić się, że urządzenia
              działają z odpowiednim ciśnieniem i w wymaganym zakresie zużycia
              gazu.
            </p>
            Po przeglądzie wystawiane jest stosowne potwierdzenie lub protokół.
          </>
        ),
      },
      {
        id: 45,
        question: "Ile czasu trwa przegląd instalacji gazowej?",
        answer: (
          <>
            <p>
              Przegląd instalacji gazowej trwa zwykle od jednej do dwóch godzin,
              w zależności od wielkości nieruchomości i liczby urządzeń
              gazowych.
            </p>
          </>
        ),
      },
      {
        id: 46,
        question: "Czy mogę samodzielnie wykonać przegląd instalacji gazowej?",
        answer: (
          <>
            <p>
              Nie. Tylko osoba posiadająca odpowiednie uprawnienia gazowe i
              przeszkolenie może przeprowadzić przegląd instalacji gazowej
              zgodnie z przepisami.
            </p>
          </>
        ),
      },
      {
        id: 47,
        question: "Co zrobić, jeśli poczuję zapach gazu w domu?",
        answer: (
          <>
            <p>
              Wycieki gazu są bardzo niebezpieczne. W przypadku wyczucia gazu
              należy:
            </p>
            <ul className="mb-1 list-disc space-y-2 pl-5">
              <li>
                Nie używać włączników elektrycznych ani urządzeń, które mogą
                wywołać iskrę.
              </li>
              <li>Wyłączyć wszystkie urządzenia gazowe.</li>
              <li>
                Otworzyć wszystkie drzwi i okna, aby przewietrzyć pomieszczenia.
              </li>
              <li>Zakazać palenia i używania otwartego ognia.</li>
              <li>
                Zakręcić zawór gazowy przy liczniku lub butli (jeśli to
                możliwe).
              </li>
              <li>
                Skontaktować się z pogotowiem gazowym lub licencjonowanym
                specjalistą jak najszybciej.
              </li>
            </ul>
          </>
        ),
      },
      {
        id: 48,
        question: "Jak bardzo przegląd gazowy zakłóci mój dzień?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd może powodować drobne niedogodności, ponieważ specjalista
              musi mieć dostęp do wszystkich urządzeń i przewodów gazowych.
            </p>
            <p>
              Jeśli dostęp jest zapewniony, specjalista w miarę możliwości
              ogranicza zakłócenia do minimum.
            </p>
          </>
        ),
      },
      {
        id: 49,
        question: "Czym jest tlenek węgla i dlaczego jest niebezpieczny",
        answer: (
          <>
            {" "}
            <p className="mb-2">
              Tlenek węgla (CO) to bezwonny, bezbarwny i bezsmakowy gaz, którego
              nie da się wykryć bez specjalistycznego sprzętu. Powstaje w wyniku
              niecałkowitego spalania paliw. Po wdychaniu zastępuje tlen we
              krwi, prowadząc do zatrucia.
            </p>
            <p className="mb-2">
              Objawy mogą przypominać grypę, zatrucie pokarmowe lub
              przeziębienie i obejmują m.in. bóle głowy, zawroty głowy,
              nudności, duszności, osłabienie czy ból w klatce piersiowej.
            </p>
          </>
        ),
      },
      {
        id: 50,
        question: "Które urządzenia mogą emitować tlenek węgla?",
        answer: (
          <>
            {" "}
            <p className="mb-2">
              Każde urządzenie gazowe może emitować tlenek węgla, jeśli jest
              niesprawne. Szczególne ryzyko stwarzają nieserwisowane piece
              gazowe oraz używanie urządzeń przenośnych (np. grilli) we
              wnętrzach bez wentylacji.
            </p>
          </>
        ),
      },

      {
        id: 51,
        question:
          "Jakie są konsekwencje braku regularnych przeglądów gazowych?",
        answer: (
          <>
            <p className="mb-2">
              Zgodnie z art. 62 ust. 1 pkt 1 lit. c Prawa budowlanego, przegląd
              instalacji gazowej musi być przeprowadzany co roku przez
              uprawnionego specjalistę. Zaniedbanie tej czynności może
              skutkować zagrożeniem życia, odpowiedzialnością cywilną, a nawet
              karami finansowymi.
            </p>
          </>
        ),
      },
      {
        id: 52,
        question:
          "Dlaczego mój zawór ciśnieniowo-temperaturowy (PTR) jest niezgodny z przepisami?",
        answer: (
          <>
            <p>
              Zawór PTR musi odprowadzać wodę o temperaturze nawet do 99°C w
              bezpieczne miejsce. Najczęstszą przyczyną niezgodności jest
              skierowanie odpływu w niebezpieczne miejsce.
            </p>
          </>
        ),
      },
      {
        id: 53,
        question: "Co oznacza pęknięty wymiennik ciepła?",
        answer: (
          <>
            <p>
              Wymiennik ciepła musi być szczelny, by nie dochodziło do emisji
              tlenku węgla. W razie wykrycia pęknięcia urządzenie zostaje
              odłączone z powodu zagrożenia.
            </p>
          </>
        ),
      },
      {
        id: 54,
        question: "Czym jest podciśnienie?",
        answer: (
          <>
            <p>
              Podciśnienie powstaje, gdy okapy kuchenne lub wentylatory
              wyciągowe powodują zasysanie powietrza z pomieszczeń. Może to
              doprowadzić do cofania się spalin z urządzeń gazowych, co stanowi
              zagrożenie.
            </p>
          </>
        ),
      },
      {
        id: 55,
        question: "Czym się różni niezgodność krytyczna od niekrytycznej?",
        answer: (
          <>
            <p>
              Niezgodność krytyczna oznacza bezpośrednie zagrożenie i wymaga
              natychmiastowej naprawy. Niekrytyczna nie stanowi zagrożenia w
              danym momencie, ale jej usunięcie jest zalecane.
            </p>
          </>
        ),
      },
      {
        id: 56,
        question: "Dlaczego muszę przenieść regulator gazowy?",
        answer: (
          <>
            <p>
              Aby wykonać pełen przegląd instalacji, regulator musi być dostępny
              dla specjalisty do testów ciśnienia i regulacji.
            </p>
          </>
        ),
      },
      {
        id: 57,
        question: "Dlaczego piec na poddaszu nie został objęty przeglądem?",
        answer: (
          <>
            <p>
              Przepisy BHP wymagają, aby dostęp do pieca był bezpieczny i
              zapewniony poprzez platformę techniczną. Jeśli jej brak,
              wykonanie przeglądu może być niemożliwe.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przeglądy gazowe w przyczepach kempingowych",
    items: [
      {
        id: 61,
        question:
          "Na czym polega przegląd instalacji gazowej w przyczepie kempingowej?",
        answer:
          "Przegląd instalacji gazowej w przyczepie to kompleksowa kontrola wszystkich instalacji, urządzeń i armatury gazowej. Inspekcje wykonywane są zgodnie z polskimi normami i wymaganiami bezpieczeństwa. W niektórych przypadkach przyczepa może wymagać dodatkowych prac, aby spełnić wymagania przed wydaniem zaświadczenia o zgodności.",
      },
      {
        id: 62,
        question:
          "Dlaczego zgodność instalacji gazowej w przyczepie kempingowej jest ważna?",
        answer:
          "Zgodność instalacji gazowej gwarantuje bezpieczeństwo użytkowników i spełnienie wymogów prawnych. Jest to również istotne przy rejestracji pojazdu – zgodność musi być potwierdzana przy każdej sprzedaży lub modyfikacji systemu gazowego.",
      },
      {
        id: 63,
        question:
          "Kto jest uprawniony do przeprowadzenia przeglądu instalacji gazowej w przyczepie?",
        answer:
          "Przeglądu instalacji gazowej może dokonać tylko licencjonowany gazownik, który sprawdzi, czy instalacje i urządzenia spełniają normy bezpieczeństwa i przepisy prawa.",
      },
      {
        id: 64,
        question:
          "Jak często należy wykonywać przegląd instalacji gazowej w przyczepie?",
        answer:
          "Zaleca się coroczne kontrole. System gazowy przyczepy należy również sprawdzić po każdej modyfikacji urządzeń gazowych, po naprawach oraz przed sprzedażą.",
      },
      {
        id: 65,
        question: "Jak przygotować przyczepę kempingową do przeglądu gazowego?",
        answer: `Upewnij się, że:
  - Przyczepa stoi na równym podłożu.
  - Jest zainstalowany punkt testowy gazu.
  - Butla gazowa jest napełniona.
  - Wszystkie urządzenia działają prawidłowo.
  - Masz reduktor dwustopniowy z zabezpieczeniem nadciśnieniowym.
  - Używasz wyłącznie urządzeń z polską lub europejską certyfikacją.`,
      },
      {
        id: 66,
        question: "Czy są przepisy dotyczące przechowywania gazu w przyczepie?",
        answer:
          "Gaz powinien być przechowywany w specjalnej komorze na zewnątrz przyczepy, szczelnej wobec wnętrza, dobrze wentylowanej i odpornej na korozję. Powinna być oznakowana i zawierać wyłącznie zabezpieczone butle gazowe oraz armaturę.",
      },
      {
        id: 67,
        question:
          "Co się dzieje, jeśli moja przyczepa nie przejdzie przeglądu zgodności?",
        answer:
          "Jeśli przyczepa nie przejdzie przeglądu, otrzymasz raport z wyszczególnionymi pracami potrzebnymi do osiągnięcia zgodności. Możesz zlecić je dowolnemu wykwalifikowanemu gazownikowi – platforma inspektomat.pl pomoże Ci znaleźć specjalistę w Twoim regionie.",
      },
      {
        id: 68,
        question:
          "Jak różne rodzaje gazu wpływają na zgodność instalacji w przyczepie?",
        answer:
          "Różne rodzaje gazu, np. propan i butan, mają różne wymagania dotyczące przechowywania, ustawień ciśnienia i zgodności urządzeń. Przegląd potwierdza, że instalacja w przyczepie jest odpowiednio skonfigurowana dla używanego gazu.",
      },
      {
        id: 69,
        question:
          "Jakie urządzenia bezpieczeństwa są sprawdzane podczas przeglądu?",
        answer:
          "Podczas przeglądu technik kontroluje m.in. czujniki gazu, zawory odcinające awaryjne oraz reduktory ciśnienia.",
      },
      {
        id: 70,
        question:
          "Dlaczego wentylacja w przyczepie jest ważna dla bezpieczeństwa gazowego?",
        answer:
          "Odpowiednia wentylacja jest kluczowa, aby zapobiec gromadzeniu się niebezpiecznych oparów gazu. Podczas przeglądu technik sprawdza, czy system wentylacyjny działa prawidłowo i spełnia przepisy.",
      },
    ],
  },
  {
    title: "FAQ - modernizacja tablic rozdzielczych",
    items: [
      {
        id: 71,
        question:
          "Czym jest tablica rozdzielcza i dlaczego warto ją modernizować?",
        answer:
          "Tablica rozdzielcza zarządza przepływem prądu i rozdziela go do obwodów i urządzeń. Modernizacja pozwala bezpiecznie obsłużyć większe obciążenie elektryczne, zmniejsza ryzyko pożaru lub porażenia prądem. Stara lub przeciążona tablica stanowi poważne zagrożenie dla mieszkańców. Modernizacja może też poprawić efektywność energetyczną, obniżyć rachunki i zwiększyć wartość nieruchomości. Niespełnianie norm może skutkować karami finansowymi.",
      },
      {
        id: 72,
        question:
          "Jakie są kluczowe zmiany w przepisach wpływające na modernizację tablic?",
        answer:
          "Nowe przepisy w Polsce wymagają, by tablice rozdzielcze spełniały najnowsze normy bezpieczeństwa elektrycznego. Muszą być wyposażone w urządzenia wykrywania łuku (AFDD) zapobiegające pożarom od łuku elektrycznego. Muszą też mieć wyłączniki różnicowoprądowe (RCD), które odcinają zasilanie w przypadku wykrycia zwarcia lub upływu prądu. Modernizacja może wymagać wymiany okablowania, przełączników i innych komponentów.",
      },
      {
        id: 73,
        question: "Jak rozpoznać, że moja tablica wymaga modernizacji?",
        answer:
          "Częste wyłączanie bezpieczników, migotanie świateł, zapach spalenizny lub iskrzące przełączniki to typowe sygnały. Aby ocenić zgodność z przepisami i konieczność modernizacji, najlepiej skonsultować się z licencjonowanym elektrykiem.",
      },
      {
        id: 74,
        question: "Jakie są konsekwencje braku modernizacji tablicy?",
        answer:
          "Brak modernizacji i niespełnianie norm może prowadzić do kar finansowych. To też duże zagrożenie dla mieszkańców. W razie wypadku ubezpieczyciel może odmówić wypłaty odszkodowania, a właściciel może ponieść odpowiedzialność za obrażenia lub zniszczenia.",
      },
      {
        id: 75,
        question: "Czy mogę samodzielnie modernizować tablicę rozdzielczą?",
        answer:
          "Nie, modernizację tablicy może przeprowadzić wyłącznie licencjonowany elektryk. Samodzielna próba jest niebezpieczna, grozi obrażeniami lub uszkodzeniem mienia, a także skutkuje brakiem zgodności z obowiązującymi przepisami.",
      },
    ],
  },
  {
    title: "FAQ - Czujniki Dymu Połączone w Sieć",
    items: [
      {
        id: 81,
        question: "Czym jest czujnik dymu połączony w sieć?",
        answer: (
          <p>
            To system czujników, które są ze sobą połączone – jeśli jeden
            wykryje dym, uruchamiają się wszystkie jednocześnie. Zapewnia to
            szybsze ostrzeżenie dla osób znajdujących się w różnych częściach
            budynku.
          </p>
        ),
      },
      {
        id: 82,
        question: "Czym różni się czujnik połączony od tradycyjnego?",
        answer: (
          <p>
            Tradycyjne czujniki działają niezależnie. Połączone w sieć
            komunikują się ze sobą, co zwiększa szanse na szybką ewakuację –
            szczególnie w dużych domach.
          </p>
        ),
      },
      {
        id: 83,
        question: "Dlaczego warto zainstalować czujniki połączone w sieć?",
        answer: (
          <p>
            Dają lepszą ochronę – ogień w piwnicy czy garażu może być wykryty
            wcześniej, a sygnał dotrze do całego domu. To ważne dla rodzin i
            większych budynków.
          </p>
        ),
      },
      {
        id: 84,
        question: "Jak komunikują się czujniki połączone w sieć?",
        answer: (
          <p>
            Najczęściej przez połączenie radiowe (bezprzewodowe), choć niektóre
            starsze modele mogą być połączone przewodowo.
          </p>
        ),
      },
      {
        id: 85,
        question: "Na jakim zasilaniu działają takie czujniki?",
        answer: (
          <p>
            Mogą być zasilane z sieci 230V z baterią awaryjną lub wyłącznie
            bateryjnie. Najlepiej wybrać wersje łączące oba źródła zasilania dla
            bezpieczeństwa w razie awarii prądu.
          </p>
        ),
      },
      {
        id: 86,
        question: "Ile czujników można połączyć w jednej instalacji?",
        answer: (
          <p>
            Zazwyczaj od 12 do 24, zależnie od producenta. Wystarczy to do
            zabezpieczenia całego domu lub mieszkania.
          </p>
        ),
      },
      {
        id: 87,
        question:
          "Czy czujniki połączone w sieć można połączyć z systemem smart home?",
        answer: (
          <p>
            Tak, nowoczesne modele obsługują integrację z aplikacjami i
            systemami inteligentnego domu. Pozwalają one otrzymywać
            powiadomienia na telefon i monitorować czujniki zdalnie.
          </p>
        ),
      },
      {
        id: 88,
        question: "Gdzie najlepiej zamontować czujniki w domu?",
        answer: (
          <p>
            W każdej sypialni, na korytarzach i na każdej kondygnacji. Unikaj
            kuchni i łazienek – para i dym z gotowania mogą wywoływać fałszywe
            alarmy.
          </p>
        ),
      },
      {
        id: 89,
        question: "Jak często należy testować i konserwować czujniki?",
        answer: (
          <p>
            Testuj co miesiąc przyciskiem testowym. Wymieniaj baterie co roku
            (chyba że są to baterie długowieczne). Cały czujnik należy wymienić
            co 10 lat lub zgodnie z zaleceniami producenta.
          </p>
        ),
      },
      {
        id: 90,
        question: "Czy są rekomendowane modele do określonych budynków?",
        answer: (
          <p>
            Tak – różne budynki (np. bloki, domy wolnostojące) mogą mieć inne
            wymagania. Skonsultuj wybór z ekspertem lub zapoznaj się z lokalnymi
            przepisami ppoż.
          </p>
        ),
      },
    ],
  },
  {
    title: "FAQ - Rozwiązania dla Właścicieli Nieruchomości na Wynajem",
    items: [
      {
        id: 101,
        question:
          "Jakie przepisy muszę spełniać jako właściciel mieszkania lub domu na wynajem?",
        answer: (
          <>
            <p>
              Jako właściciel masz obowiązek zapewnić zgodność z polskim prawem
              budowlanym i przepisami PPOŻ. Obejmuje to m.in.:
              <ul className="mb-1 list-disc space-y-2 pl-5">
                <li>
                  Przegląd instalacji elektrycznej co 5 lat i gazowej co rok
                  przez uprawnionego specjalistę (art. 62 Prawa budowlanego).
                </li>
                <li>
                  Czujniki dymu muszą być zamontowane, sprawne, testowane co
                  najmniej raz w roku i mieć wymieniane baterie. Najemca
                  powinien zgłaszać niesprawność.
                </li>
              </ul>
            </p>
          </>
        ),
      },
      {
        id: 102,
        question: "Kiedy mogę umówić wizytę technika?",
        answer: (
          <>
            <p>
              Wizytę umawiasz bezpośrednio z wybraną firmą. Większość
              specjalistów z naszej bazy przyjmuje od poniedziałku do piątku
              w godzinach 8:00–18:00; dostępność w soboty zależy od wykonawcy.
            </p>
          </>
        ),
      },
      {
        id: 103,
        question: "Ile trwa wizyta technika?",
        answer: (
          <>
            <p>
              Czas trwania zależy od usługi:
              <ul className="mb-1 list-disc space-y-2 pl-5">
                <li>Elektryka: ok. 1–2 godziny</li>
                <li>Gaz: ok. 1–3 godziny</li>
                <li>Czujniki dymu: ok. 15 minut</li>
              </ul>
            </p>
          </>
        ),
      },
      {
        id: 104,
        question:
          "Co polecacie właścicielom, którzy chcą mieć wszystko pod kontrolą?",
        answer: (
          <>
            <p>
              Skorzystaj z platformy inspektomat.pl — wyślij jedno
              zapytanie i porównaj oferty kilku certyfikowanych firm działających
              w Twoim mieście. Wybierz wykonawcę, ustal termin i otrzymaj
              dokumentację po każdym przeglądzie.
            </p>
          </>
        ),
      },
      {
        id: 105,
        question:
          "Jakie są konsekwencje prawne i praktyczne braku regularnych przeglądów instalacji elektrycznej, gazowej i czujników dymu w nieruchomościach na wynajem?",
        answer: (
          <>
            <p>
              W Polsce właściciel nieruchomości ma obowiązek zapewnienia
              bezpieczeństwa lokalu zgodnie z Prawem budowlanym, ustawą o
              ochronie przeciwpożarowej oraz Kodeksem cywilnym. Brak cyklicznych
              przeglądów instalacji elektrycznej, gazowej i niesprawne czujniki
              dymu może skutkować:
            </p>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>odpowiedzialnością cywilną i karną w razie wypadku,</li>
              <li>
                karami administracyjnymi lub sankcjami z nadzoru budowlanego,
              </li>
              <li>nieważnością polisy ubezpieczeniowej,</li>
              <li>zagrożeniem życia i zdrowia najemców.</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - Rozwiązania dla Zarządców Nieruchomości",
    items: [
      {
        id: 111,
        question:
          "Jakie normy bezpieczeństwa powinienem uwzględniać przy instalacjach elektrycznych, gazowych i czujnikach dymu w wynajmowanych lokalach?",
        answer: (
          <>
            <p>
              Jako zarządca nieruchomości masz obowiązek zapewnić zgodność
              lokali z obowiązującymi przepisami budowlanymi oraz PPOŻ w imieniu
              właścicieli. Wymagane są:
            </p>
            <ul className="mb-1 list-disc space-y-2 pl-5">
              <li>
                Przegląd instalacji elektrycznej co 5 lat i gazowej co rok,
                wykonywane przez uprawnionych specjalistów (art. 62 Prawa
                budowlanego).
              </li>
              <li>
                Sprawne i przetestowane czujniki dymu – minimum raz w roku.
                Wymiana baterii oraz naprawa po zgłoszeniu usterki przez
                najemcę.
              </li>
            </ul>
            <p>
              Skorzystaj z inspektomat.pl, by szybko znaleźć
              certyfikowane firmy i umówić przeglądy dla wszystkich
              zarządzanych lokali.
            </p>
          </>
        ),
      },
      {
        id: 112,
        question: "Ile czasu trwają poszczególne przeglądy?",
        answer: (
          <>
            <p>Szacowany czas wykonania usługi:</p>
            <ul className="mb-1 list-disc space-y-2 pl-5">
              <li>Przegląd elektryczny: 1–2 godziny</li>
              <li>Przegląd gazowy: 1–3 godziny</li>
              <li>Kontrola czujników dymu: ok. 15 minut</li>
            </ul>
            <p>
              Czas może się wydłużyć w zależności od liczby urządzeń lub
              potrzeby wykonania naprawy.
            </p>
          </>
        ),
      },
      {
        id: 113,
        question: "Jak przygotować lokal do przeglądów?",
        answer: (
          <p>
            Wybrany wykonawca przed każdą wizytą przekazuje szczegółowe
            informacje dopasowane do rodzaju nieruchomości i zakresu kontroli.
          </p>
        ),
      },
      {
        id: 114,
        question: "Jaką usługę polecacie zapracowanym zarządcom nieruchomości?",
        answer: (
          <p>
            Skorzystaj z inspektomat.pl — wyślij jedno zapytanie i
            porównaj oferty certyfikowanych firm. Platforma ułatwia planowanie
            terminów przeglądów i gromadzenie dokumentacji dla wszystkich
            zarządzanych lokali.
          </p>
        ),
      },
      {
        id: 115,
        question:
          "Jakie są konsekwencje braku cyklicznych przeglądów instalacji i czujników?",
        answer: (
          <p>
            Obowiązkiem zarządcy jest nadzorowanie zgodności z przepisami dla
            wszystkich zarządzanych lokali. Zaniedbanie przeglądów może
            skutkować odpowiedzialnością prawną, brakiem ważności ubezpieczenia
            oraz zagrożeniem życia lokatorów.
          </p>
        ),
      },
    ],
  },
  {
    title: "FAQ - Rozwiązania dla Właścicieli Domów",
    items: [
      {
        id: 121,
        question:
          "Jakie standardy bezpieczeństwa powinienem znać w zakresie instalacji elektrycznych, urządzeń gazowych i czujników dymu w moim domu?",
        answer: (
          <>
            <p>
              Zgodnie z polskim Prawem budowlanym (art. 62) Twoje obowiązki
              obejmują:
              <ul className="mt-1 list-disc space-y-2 pl-5">
                <li>
                  Przegląd instalacji elektrycznej co 5 lat i gazowej co rok
                  przez uprawnionych specjalistów. Właściciel ma obowiązek
                  udostępnić daty ostatnich kontroli na żądanie.
                </li>
                <li className="mb-1">
                  Czujniki dymu: Muszą być poprawnie zainstalowane, konserwowane
                  i testowane zgodnie z instrukcją producenta co najmniej raz w
                  roku. Baterie należy wymieniać w razie potrzeby. Jeśli czujnik
                  dymu jest niesprawny, obowiązkiem najemcy jest zgłoszenie tego
                  pisemnie.
                </li>
              </ul>
            </p>
          </>
        ),
      },
      {
        id: 122,
        question: "Ile trwa każda wizyta serwisowa?",
        answer: (
          <>
            <p>
              Czas trwania wizyty zależy od rodzaju usługi:
              <ul className="mb-1 mt-1 list-disc space-y-2 pl-5">
                <li>Przegląd elektryczny: 1–2 godziny</li>
                <li>Przegląd gazowy: 1–3 godziny</li>
                <li>Kontrola czujników dymu: około 15 minut</li>
              </ul>
            </p>
            <p>
              Czas może się różnić w zależności od liczby urządzeń lub
              konieczności pilnych napraw.
            </p>
          </>
        ),
      },
      {
        id: 123,
        question: "Jak przygotować nieruchomość do przeglądów?",
        answer: (
          <>
            <p>
              Wybrany wykonawca poinformuje Cię o wszelkich wymaganych
              przygotowaniach, zależnie od rodzaju usług i typu nieruchomości.
            </p>
          </>
        ),
      },
      {
        id: 124,
        question:
          "Jak łatwo zadbać o zgodność z przepisami i nie zapominać o terminach?",
        answer: (
          <p>
            Skorzystaj z inspektomat.pl — porównaj oferty
            certyfikowanych firm i umów przeglądy z wyprzedzeniem. Po każdej
            kontroli otrzymasz protokół, który potwierdza spełnienie wymogów.
          </p>
        ),
      },
      {
        id: 125,
        question:
          "Jakie są potencjalne konsekwencje braku regularnych przeglądów instalacji elektrycznej, gazowej i czujników dymu?",
        answer: (
          <p>
            Zgodnie z polskim Prawem budowlanym właściciel nieruchomości ma
            obowiązek zapewniać przegląd instalacji elektrycznej co 5 lat i
            gazowej co rok. Zaniechanie tych obowiązków grozi
            odpowiedzialnością prawną, nieważnością polisy ubezpieczeniowej
            oraz zagrożeniem życia mieszkańców.
          </p>
        ),
      },
    ],
  },
  {
    title: "FAQ - Rozwiązania dla Zarządców Budynków",
    items: [
      {
        id: 131,
        question:
          "Jakie standardy bezpieczeństwa powinienem znać w zakresie instalacji elektrycznych, urządzeń gazowych i czujników dymu w lokalach, którymi zarządzam?",
        answer: (
          <>
            <p>
              Zgodnie z Prawem budowlanym (art. 62) Twoje obowiązki obejmują:
            </p>
            <ul className="mb-1 list-disc space-y-2 pl-5">
              <li>
                Przeglądy instalacji elektrycznej co 5 lat i gazowej co rok
                przez uprawnionych specjalistów. Najemcy mają prawo znać daty
                ostatnich kontroli na żądanie.
              </li>
              <li>
                Czujniki dymu: Muszą być poprawnie zainstalowane, konserwowane i
                testowane zgodnie z instrukcją producenta co najmniej raz w
                roku. Baterie należy wymieniać w razie potrzeby. Jeśli czujnik
                dymu jest niesprawny, obowiązkiem najemcy jest zgłoszenie tego
                pisemnie.
              </li>
            </ul>
          </>
        ),
      },
      {
        id: 132,
        question: "Ile trwają poszczególne przeglądy bezpieczeństwa?",
        answer: (
          <>
            <p>Czas trwania przeglądu zależy od rodzaju usługi:</p>
            <ul className="mb-1 list-disc space-y-2 pl-5">
              <li>Przegląd elektryczny: 1–2 godziny</li>
              <li>Przegląd gazowy: 1–3 godziny</li>
              <li>Kontrola czujników dymu: około 15 minut</li>
            </ul>
            <p>
              Czas może się różnić w zależności od liczby urządzeń lub
              konieczności pilnych napraw.
            </p>
          </>
        ),
      },
      {
        id: 133,
        question: "Jak przygotować nieruchomość do przeglądów?",
        answer: (
          <>
            <p>
              Wybrany wykonawca udzieli szczegółowych instrukcji dostosowanych
              do rodzaju nieruchomości i planowanych przeglądów.
            </p>
          </>
        ),
      },
      {
        id: 134,
        question:
          "Jak efektywnie zarządzać terminami przeglądów w wielu lokalach?",
        answer: (
          <p>
            Skorzystaj z inspektomat.pl — jednym zapytaniem dotrzesz
            do certyfikowanych firm w całej Polsce. Możesz planować przeglądy
            dla wielu nieruchomości i zbierać protokoły w jednym miejscu.
          </p>
        ),
      },
      {
        id: 135,
        question:
          "Jakie są potencjalne konsekwencje braku regularnych przeglądów instalacji elektrycznej, gazowej i czujników dymu?",
        answer: (
          <p>
            Zgodnie z polskim Prawem budowlanym (art. 62) zarządcy budynków
            muszą zapewnić przegląd instalacji elektrycznej co 5 lat i gazowej
            co rok przez uprawnionych specjalistów. Brak działań może prowadzić
            do poważnych konsekwencji prawnych i zagrożenia życia mieszkańców.
          </p>
        ),
      },
    ],
  },
  {
    title: "FAQ - Rozwiązania dla Agencji Nieruchomości",
    items: [
      {
        id: 141,
        question:
          "Jakie standardy bezpieczeństwa powinienem znać w zakresie instalacji elektrycznych, gazowych i czujników dymu w nieruchomościach, którymi zarządzamy?",
        answer: (
          <>
            <p>
              Zgodnie z polskim Prawem budowlanym (art. 62) Twoje obowiązki
              obejmują:
              <ul className="mb-1 mt-1 list-disc space-y-2 pl-5">
                <li>Przegląd instalacji elektrycznej co 5 lat i gazowej co rok przez uprawnionych specjalistów</li>
                <li>
                  Testy i konserwacja czujników dymu przynajmniej raz w roku
                </li>
              </ul>
            </p>
          </>
        ),
      },
      {
        id: 142,
        question: "Ile zazwyczaj trwają poszczególne przeglądy?",
        answer: (
          <>
            <p>
              To zależy od usługi:
              <ul className="mb-1 mt-1 list-disc space-y-2 pl-5">
                <li>Instalacje elektryczne: 1–2 godziny</li>
                <li>Instalacje gazowe: 1–3 godziny</li>
                <li>Czujniki dymu: ok. 15 minut</li>
              </ul>
            </p>
          </>
        ),
      },
      {
        id: 143,
        question: "Jak przygotować nieruchomość do przeglądów?",
        answer: (
          <>
            <p>
              Wybrany wykonawca przeprowadzi Cię przez proces przygotowania na
              podstawie rodzaju Twojej nieruchomości i wybranych usług.
            </p>
          </>
        ),
      },
      {
        id: 144,
        question:
          "Jak agencja nieruchomości może efektywnie zarządzać przeglądami dla całego portfela?",
        answer: (
          <p>
            Skorzystaj z inspektomat.pl — jednym zapytaniem dotrzesz
            do certyfikowanych firm obsługujących wiele lokalizacji. Agencje
            mogą planować przeglądy dla całego portfela nieruchomości i
            archiwizować protokoły w jednym miejscu.
          </p>
        ),
      },
      {
        id: 145,
        question:
          "Jakie są konsekwencje zaniechania regularnych przeglądów elektrycznych, gazowych i przeciwpożarowych?",
        answer: (
          <p>
            Obowiązkiem agencji nieruchomości jest zapewnienie przeglądów
            instalacji elektrycznej co 5 lat i gazowej co rok przez
            uprawnionych fachowców. Brak działań może prowadzić do sankcji
            prawnych i zagrożeń dla najemców.
          </p>
        ),
      },
    ],
  },
  {
    title: "Wymagania Prawne",
    items: [
      {
        id: 151,
        question: "Bezpieczeństwo Instalacji Elektrycznych",
        answer: (
          <>
            <p className="mb-2 text-center text-xl font-bold">
              Obowiązkowe kontrole instalacji elektrycznej – zgodnie z polskim
              prawem
            </p>
            <p className="mb-4">
              Zgodnie z ustawą Prawo budowlane oraz Rozporządzeniem Ministra
              Infrastruktury, właściciel lub zarządca budynku ma obowiązek
              wykonywania okresowej kontroli instalacji elektrycznej co najmniej
              raz na 5 lat. Kontrola obejmuje stan techniczny, sprawność
              połączeń, zabezpieczeń i środków ochrony przeciwporażeniowej.
            </p>
            <p className="mb-4">
              Kontrola powinna być przeprowadzana przez osobę z odpowiednimi
              uprawnieniami SEP (Stowarzyszenie Elektryków Polskich).
            </p>
            <p className="mb-4">
              W nowoczesnych instalacjach wymagane jest również stosowanie
              wyłączników różnicowoprądowych (RCD) oraz zabezpieczeń
              nadprądowych zgodnych z obowiązującymi normami technicznymi.
            </p>
            <ul className="mb-4 list-disc">
              <li className="mb-2 mr-2 flex h-10 items-center">
                <Icon8Checkmark className="mr-4 h-7 w-7 flex-shrink-0" />
                Regularna kontrola rozdzielnic, przewodów i gniazd
                elektrycznych.
              </li>
              <li className="mb-2 mr-2 flex h-10 items-center">
                <Icon8Checkmark className="mr-4 h-7 w-7 flex-shrink-0" />
                Weryfikacja obecności i sprawności wyłączników
                różnicowoprądowych.
              </li>
            </ul>{" "}
          </>
        ),
      },
      {
        id: 152,
        question: "Bezpieczeństwo Instalacji Gazowych",
        answer: (
          <>
            <p className="mb-2 text-center text-xl font-bold">
              Przeglądy instalacji gazowej – obowiązki właściciela lub zarządcy
            </p>
            <p>
              Właściciel lub zarządca budynku ma obowiązek zapewnienia
              regularnych kontroli instalacji gazowych zgodnie z przepisami
              Prawa budowlanego. Przegląd powinien być wykonywany co najmniej
              raz w roku przez osobę posiadającą odpowiednie uprawnienia gazowe.
            </p>
            <ul className="mb-1 list-disc">
              <li className="mb-2 flex h-20 items-center">
                <Icon8Checkmark className="mr-4 h-7 w-7 flex-shrink-0" />
                Kontrola obejmuje szczelność instalacji, stan przewodów i
                urządzeń gazowych.
              </li>
              <li className="mb-2 flex h-20 items-center">
                <Icon8Checkmark className="mr-4 h-7 w-7 flex-shrink-0" />
                Brak wykonania przeglądu może skutkować sankcjami
                administracyjnymi i zwiększa ryzyko awarii.
              </li>
            </ul>
          </>
        ),
      },
      {
        id: 153,
        question: "Bezpieczeństwo Instalacji Przeciwpożarowych",
        answer: (
          <>
            <p className="mb-2 text-center text-xl font-bold">
              Czujniki dymu – obowiązki wynikające z przepisów przeciwpożarowych
            </p>
            <ol>
              <li>
                Właściciel lub zarządca nieruchomości ma obowiązek:
                <ul className="mb-1 mt-1 list-disc space-y-2">
                  <li className="mb-2 mr-2 flex h-10 items-center">
                    <Icon8Checkmark className="mr-4 h-7 w-7 flex-shrink-0" />
                    zapewnić sprawność działania czujników dymu we wszystkich
                    lokalach,
                  </li>
                  <li className="mb-2 mr-2 flex h-10 items-center">
                    <Icon8Checkmark className="mr-4 h-7 w-7 flex-shrink-0" />
                    dokonywać testów zgodnie z zaleceniami producenta co
                    najmniej raz w roku,
                  </li>
                  <li className="mb-2 mr-2 flex h-10 items-center">
                    <Icon8Checkmark className="mr-4 h-7 w-7 flex-shrink-0" />
                    wymieniać baterie w czujnikach, jeśli są wymagane.
                  </li>
                </ul>
              </li>
              <li>
                W razie zgłoszenia niesprawności czujnika, należy bezzwłocznie
                zlecić jego naprawę lub wymianę. W przypadku czujników
                zasilanych sieciowo, wymiana powinna być przeprowadzona przez
                osobę uprawnioną.
              </li>
              <li>
                Przed wynajmem lokalu najemcy należy przekazać instrukcję
                obsługi czujników, w tym:
                <ul className="mb-1 mt-1 list-disc space-y-2">
                  <li className="mb-2 mr-2 flex h-10 items-center">
                    <Icon8Checkmark className="mr-4 h-7 w-7 flex-shrink-0" />
                    sposób działania i testowania urządzeń,
                  </li>
                  <li className="mb-2 mr-2 flex h-10 items-center">
                    <Icon8Checkmark className="mr-4 h-7 w-7 flex-shrink-0" />
                    obowiązek nieingerowania w czujniki oraz zgłaszania usterek.
                  </li>
                </ul>
              </li>
              <li>
                Najemca ma obowiązek zgłosić niesprawność czujnika zaraz po jej
                wykryciu.
                <p className="mb-4 mt-4 text-center italic">
                  Uwaga – Zgodnie z Rozporządzeniem Ministra Infrastruktury, w
                  każdym budynku mieszkalnym wymagane jest stosowanie czujników
                  dymu lub systemów sygnalizacji pożaru.
                </p>
              </li>
            </ol>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - próba szczelności gazu",
    items: [
      {
        id: 241,
        question: "Na czym polega próba szczelności instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              To badanie wykonywane po to, aby potwierdzić, czy instalacja lub
              jej badany odcinek jest szczelny i może zostać bezpiecznie
              uruchomiony albo odebrany po wykonanych pracach.
            </p>
            <p>
              Celem próby nie jest ogólna ocena eksploatacyjna budynku, tylko
              jednoznaczne sprawdzenie, czy na przewodach, połączeniach i
              armaturze nie występują nieszczelności.
            </p>
          </>
        ),
      },
      {
        id: 242,
        question: "Kiedy wykonuje się próbę szczelności gazu?",
        answer: (
          <>
            <p className="mb-2">
              Najczęściej po wykonaniu nowej instalacji, po przebudowie, po
              naprawie, po wymianie fragmentu przewodów albo przed ponownym
              uruchomieniem instalacji, która była wyłączona z użytkowania.
            </p>
            <p>
              To badanie typowe dla momentu odbioru i uruchomienia instalacji,
              a nie zamiennik corocznego przeglądu gazowego.
            </p>
          </>
        ),
      },
      {
        id: 243,
        question: "Czy próba szczelności gazu jest obowiązkowa?",
        answer: (
          <>
            <p className="mb-2">
              Co do zasady tak, jeśli mówimy o nowej, przebudowywanej albo
              naprawianej instalacji gazowej przed jej odbiorem lub ponownym
              uruchomieniem. To badanie ma potwierdzić, że układ jest szczelny
              i może zostać bezpiecznie dopuszczony do dalszego użytkowania.
            </p>
            <p>
              Nie jest to jednak to samo co coroczny przegląd instalacji
              gazowej. Próba szczelności dotyczy konkretnego momentu po
              wykonaniu prac albo przed uruchomieniem, a nie zwykłej okresowej
              kontroli eksploatacyjnej.
            </p>
          </>
        ),
      },
      {
        id: 244,
        question:
          "Czy próbę szczelności wykonuje się przy odbiorze instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Próba szczelności bardzo często jest jednym z podstawowych
              elementów odbioru instalacji gazowej, bo pozwala potwierdzić, że
              wykonany układ jest szczelny przed przekazaniem go do użytkowania
              albo przed dalszym etapem uruchomienia.
            </p>
            <p>
              W praktyce dodatni wynik próby i protokół z badania porządkują
              dokumentację odbiorową. Jeśli wynik jest negatywny, trzeba
              najpierw zlokalizować nieszczelność, wykonać naprawę i powtórzyć
              badanie.
            </p>
          </>
        ),
      },
      {
        id: 245,
        question: "Ile kosztuje próba szczelności instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy przede wszystkim od wielkości instalacji, liczby
              badanych odcinków, złożoności układu i celu wizyty. Inaczej
              wycenia się prostą próbę w mieszkaniu lub domu, a inaczej badanie
              większej instalacji po szerszej przebudowie.
            </p>
            <p>
              Na koszt wpływa też to, czy zlecenie obejmuje wyłącznie samo
              badanie, czy również dodatkową diagnostykę po negatywnym wyniku,
              przygotowanie protokołu i lokalizację wycieku gazu.
            </p>
          </>
        ),
      },
      {
        id: 246,
        question: "Jak długo trwa próba szczelności gazu?",
        answer: (
          <>
            <p className="mb-2">
              Sam czas badania zależy od wielkości instalacji, liczby odcinków
              i stopnia skomplikowania układu. W prostych przypadkach próba może
              zamknąć się w jednej wizycie, a przy większych instalacjach lub po
              wcześniejszych problemach zajmuje wyraźnie więcej czasu.
            </p>
            <p>
              Do czasu całej usługi trzeba doliczyć przygotowanie instalacji do
              badania, sam pomiar, ocenę wyniku oraz ewentualne czynności
              diagnostyczne, jeśli pojawi się podejrzenie nieszczelności.
            </p>
          </>
        ),
      },
      {
        id: 247,
        question:
          "Czy po wykonaniu próby szczelności gazu otrzymuje się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Po wykonaniu badania standardem jest protokół, który
              potwierdza zakres próby, wynik oraz podstawowe dane instalacji i
              obiektu.
            </p>
            <p>
              Taki dokument jest potrzebny do dokumentacji technicznej,
              odbiorowej albo do dalszego uruchomienia instalacji po wykonanych
              pracach.
            </p>
          </>
        ),
      },
      {
        id: 248,
        question: "Kto może wykonać próbę szczelności instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Badanie powinien wykonywać specjalista mający właściwe
              kwalifikacje do pracy przy instalacjach gazowych i doświadczenie w
              wykonywaniu tego typu prób oraz dokumentacji.
            </p>
            <p>
              Liczy się nie tylko sam pomiar, ale też prawidłowe przygotowanie
              instalacji, interpretacja wyniku i poprawne sporządzenie
              protokołu.
            </p>
          </>
        ),
      },
      {
        id: 249,
        question:
          "Czy próbę szczelności wykonuje się w mieszkaniu i domu jednorodzinnym?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Próba szczelności może dotyczyć zarówno instalacji gazowej w
              mieszkaniu, jak i w domu jednorodzinnym, jeśli wykonano nową
              instalację, przebudowę, naprawę albo trzeba potwierdzić jej stan
              przed uruchomieniem.
            </p>
            <p>
              Zakres badania zależy od rodzaju obiektu i układu instalacji, ale
              sama zasada jest taka sama: trzeba potwierdzić, że instalacja jest
              szczelna i bezpieczna do dalszego użytkowania.
            </p>
          </>
        ),
      },
      {
        id: 250,
        question:
          "Czy próba szczelności pozwala wykryć nieszczelność instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Tak, próba szczelności pozwala potwierdzić, że instalacja jest
              nieszczelna albo szczelna. Jeśli wynik wychodzi negatywnie, wiadomo,
              że w układzie występuje problem wymagający dalszej diagnostyki.
            </p>
            <p>
              Sama próba nie zawsze wskazuje od razu dokładne miejsce wycieku.
              Do tego zwykle potrzebne jest dodatkowe wykrywanie nieszczelności
              gazu, detekcja gazu i lokalizacja wycieku przed wykonaniem
              naprawy i ponownym badaniem.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd instalacji gazowej",
    items: [
      {
        id: 261,
        question: "Czy przegląd instalacji gazowej jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Zgodnie z Prawem budowlanym instalacja gazowa podlega
              obowiązkowej kontroli okresowej wykonywanej co najmniej raz w
              roku.
            </p>
            <p>
              Obowiązek ten spoczywa na właścicielu lub zarządcy obiektu i
              dotyczy również budynków mieszkalnych jednorodzinnych wyposażonych
              w instalację gazową.
            </p>
          </>
        ),
      },
      {
        id: 262,
        question: "Na czym polega przegląd instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd polega na ocenie szczelności instalacji, stanu
              przewodów, połączeń, zaworów i podłączonych urządzeń gazowych.
            </p>
            <p>
              W praktyce sprawdza się też warunki bezpiecznego użytkowania
              urządzeń, w tym wentylację i odprowadzanie spalin, bo to one mają
              bezpośredni wpływ na ryzyko wycieku gazu i emisji tlenku węgla.
            </p>
          </>
        ),
      },
      {
        id: 263,
        question: "Co ile wykonuje się przegląd instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Minimalna częstotliwość to raz w roku. Ten termin należy
              traktować jako obowiązkowe minimum wynikające z przepisów.
            </p>
            <p>
              Jeżeli pojawia się zapach gazu, problem z urządzeniem, żółty
              płomień albo inne niepokojące objawy, kontrolę trzeba zlecić
              wcześniej, bez czekania na kolejny przegląd okresowy.
            </p>
          </>
        ),
      },
      {
        id: 264,
        question: "Czy przegląd instalacji gazowej wykonuje się w mieszkaniu?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Mieszkanie z instalacją gazową albo urządzeniami gazowymi
              również podlega kontroli, tak samo jak dom jednorodzinny.
            </p>
            <p>
              Zakres może się różnić w zależności od rodzaju nieruchomości i
              liczby urządzeń, ale sam obowiązek kontroli i potrzeba oceny
              bezpieczeństwa pozostają takie same dla mieszkań i domów.
            </p>
          </>
        ),
      },
      {
        id: 265,
        question: "Co obejmuje kontrola instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">Typowa kontrola obejmuje sprawdzenie:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>szczelności przewodów i połączeń,</li>
              <li>stanu zaworów, kurków i armatury,</li>
              <li>podłączonych urządzeń gazowych,</li>
              <li>warunków wentylacji i odprowadzania spalin,</li>
              <li>nieprawidłowości mogących wpływać na bezpieczne użytkowanie instalacji.</li>
            </ul>
          </>
        ),
      },
      {
        id: 266,
        question: "Czy po przeglądzie instalacji gazowej otrzymuje się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Po kontroli właściciel lub zarządca powinien otrzymać
              protokół potwierdzający zakres przeglądu, wynik oraz stan
              instalacji w dniu badania.
            </p>
            <p>
              Dokument jest potrzebny do dokumentacji technicznej budynku, przy
              kontroli, ubezpieczeniu, wynajmie albo planowaniu dalszych napraw
              i serwisu.
            </p>
          </>
        ),
      },
      {
        id: 267,
        question: "Kto wykonuje przegląd instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd powinien wykonywać specjalista posiadający właściwe
              kwalifikacje do pracy przy instalacjach gazowych.
            </p>
            <p>
              Liczy się nie tylko samo sprawdzenie szczelności, ale też
              poprawna ocena stanu technicznego instalacji i prawidłowe
              sporządzenie dokumentacji po kontroli.
            </p>
          </>
        ),
      },
      {
        id: 268,
        question: "Ile kosztuje przegląd instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Nie ma jednej stałej ceny, bo koszt zależy od rodzaju
              nieruchomości, wielkości instalacji, liczby urządzeń gazowych i
              zakresu kontroli.
            </p>
            <p>
              Na wycenę wpływa też to, czy chodzi o standardowy przegląd
              okresowy, czy o wizytę po problemach z instalacją, z dodatkową
              diagnostyką albo szerszą dokumentacją po kontroli.
            </p>
          </>
        ),
      },
      {
        id: 269,
        question: "Co zrobić, jeśli podczas użytkowania czuć gaz?",
        answer: (
          <>
            <p className="mb-2">
              W takiej sytuacji nie należy korzystać z otwartego ognia ani
              urządzeń mogących wywołać iskrę. Trzeba przewietrzyć
              pomieszczenia, zakręcić dopływ gazu, jeśli jest to bezpieczne, i
              wezwać pomoc.
            </p>
            <p>
              To nie jest sytuacja do czekania na zwykły przegląd roczny.
              Potrzebna jest pilna diagnostyka i sprawdzenie instalacji przez
              uprawnionego specjalistę.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd kotła gazowego",
    items: [
      {
        id: 271,
        question: "Czy przegląd kotła gazowego jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              W budynku z kotłem gazowym obowiązkowa jest coroczna kontrola
              instalacji gazowej oraz przewodów kominowych. W praktyce oznacza
              to, że stan urządzenia nie powinien pozostawać bez regularnej
              oceny.
            </p>
            <p>
              Dodatkowy, techniczny serwis samego kotła jest zwykle zalecany co
              roku przez producenta i ma znaczenie dla bezpiecznej eksploatacji
              oraz często także dla utrzymania gwarancji.
            </p>
          </>
        ),
      },
      {
        id: 272,
        question: "Jak często wykonuje się przegląd kotła gazowego?",
        answer: (
          <>
            <p className="mb-2">
              Najbezpieczniej wykonywać przegląd i serwis kotła gazowego raz w
              roku. To podejście odpowiada praktyce serwisowej i pozwala
              przygotować urządzenie do kolejnego sezonu pracy.
            </p>
            <p>
              Jeśli kocioł pracuje niestabilnie, zgłasza błędy albo pojawiają
              się objawy problemów ze spalaniem, serwis trzeba zlecić wcześniej,
              bez czekania na coroczny termin.
            </p>
          </>
        ),
      },
      {
        id: 273,
        question: "Ile kosztuje przegląd kotła gazowego?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od typu kotła, marki urządzenia, zakresu serwisu i
              tego, czy chodzi o standardowy roczny przegląd, czy o wizytę z
              dodatkowymi czynnościami diagnostycznymi.
            </p>
            <p>
              Na wycenę wpływa też to, czy w zakres wchodzi analiza spalania,
              czyszczenie, regulacja i przygotowanie dokumentacji po serwisie.
            </p>
          </>
        ),
      },
      {
        id: 274,
        question:
          "Czy przegląd kotła gazowego wykonuje się przed sezonem grzewczym?",
        answer: (
          <>
            <p className="mb-2">
              Tak, to najlepszy moment na wykonanie standardowego rocznego
              serwisu. Dzięki temu urządzenie można sprawdzić i przygotować
              zanim zacznie pracować intensywnie przez wiele miesięcy.
            </p>
            <p>
              Taki termin zmniejsza ryzyko awarii w środku sezonu grzewczego i
              pozwala spokojnie wykonać ewentualne regulacje albo naprawy.
            </p>
          </>
        ),
      },
      {
        id: 275,
        question: "Jak długo trwa serwis kotła gazowego?",
        answer: (
          <>
            <p className="mb-2">
              Czas wizyty zależy od typu kotła, jego stanu technicznego i
              zakresu czynności. Standardowy serwis zwykle mieści się w jednej
              wizycie, ale przy starszych urządzeniach albo dodatkowej
              diagnostyce może potrwać dłużej.
            </p>
            <p>
              Do czasu całej usługi trzeba doliczyć nie tylko czyszczenie, ale
              też sprawdzenie parametrów spalania, ocenę stanu podzespołów i
              ewentualną regulację urządzenia.
            </p>
          </>
        ),
      },
      {
        id: 276,
        question:
          "Czy po przeglądzie kotła gazowego otrzymuje się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Po serwisie właściciel powinien otrzymać potwierdzenie wykonanych
              czynności, a w zależności od zakresu wizyty także protokół lub
              wpis do dokumentacji serwisowej urządzenia.
            </p>
            <p>
              Taka dokumentacja porządkuje historię kotła, ułatwia kolejne
              przeglądy i może być ważna przy gwarancji, sprzedaży nieruchomości
              albo planowaniu dalszych napraw.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - odbiór instalacji gazowej",
    items: [
      {
        id: 281,
        question: "Ile kosztuje odbiór instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Cena odbioru instalacji gazowej zależy od wielkości instalacji,
              rodzaju obiektu i zakresu czynności, które mają zostać objęte
              wizytą.
            </p>
            <p>
              Inaczej wycenia się odbiór prostej instalacji w domu
              jednorodzinnym, a inaczej odbiór w lokalu usługowym albo
              większym budynku, zwłaszcza jeśli w zakres wchodzi analiza
              dokumentacji, weryfikacja protokołów i dodatkowe badania.
            </p>
          </>
        ),
      },
      {
        id: 282,
        question: "Kto wykonuje odbiór instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Odbiór instalacji gazowej powinien wykonywać specjalista mający
              właściwe kwalifikacje do oceny robót i instalacji gazowych.
            </p>
            <p>
              W praktyce w proces mogą być zaangażowani także wykonawca,
              kierownik budowy lub inwestor, ale sama techniczna weryfikacja
              powinna być prowadzona przez osobę, która potrafi ocenić zgodność
              wykonania, wyniki prób i kompletność dokumentacji.
            </p>
          </>
        ),
      },
      {
        id: 283,
        question: "Jak długo trwa odbiór instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Czas odbioru zależy od wielkości instalacji, liczby urządzeń,
              stopnia skomplikowania obiektu i tego, czy dokumentacja jest
              przygotowana kompletnie przed wizytą.
            </p>
            <p>
              Prosty odbiór może zamknąć się w jednej wizycie, ale przy
              większych inwestycjach albo brakach w dokumentach cały proces może
              potrwać dłużej i wymagać uzupełnień lub ponownej kontroli.
            </p>
          </>
        ),
      },
      {
        id: 284,
        question: "Jakie dokumenty są potrzebne do odbioru instalacji gazowej?",
        answer: (
          <>
            <p className="mb-2">
              Najczęściej potrzebne są projekt techniczny lub dokumentacja
              powykonawcza, oświadczenie kierownika budowy o zgodności wykonania
              z projektem i przepisami oraz protokół próby szczelności
              instalacji gazowej.
            </p>
            <p>
              W zależności od inwestycji potrzebne mogą być także inne protokoły
              badań i sprawdzeń, dziennik budowy, uzgodnienia branżowe albo
              dodatkowe załączniki wymagane przy zakończeniu budowy lub
              zgłoszeniu obiektu do użytkowania.
            </p>
          </>
        ),
      },
      {
        id: 285,
        question: "Czy odbiór instalacji gazowej jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Przy nowej instalacji gazowej albo istotnej przebudowie odbiór
              jest w praktyce niezbędnym etapem potwierdzenia, że roboty
              wykonano prawidłowo i że dokumentacja pozwala przejść do dalszych
              formalności.
            </p>
            <p>
              Zakres formalnych wymagań zależy od rodzaju inwestycji i trybu
              zakończenia budowy, ale bez technicznego odbioru i kompletu
              protokołów trudno bezpiecznie uruchomić instalację i domknąć
              proces odbiorowy obiektu.
            </p>
          </>
        ),
      },
      {
        id: 286,
        question: "Czy przy odbiorze wykonuje się próbę szczelności gazu?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Próba szczelności jest jednym z kluczowych elementów
              weryfikowanych przy odbiorze instalacji gazowej.
            </p>
            <p>
              To właśnie ona potwierdza, że przewody i połączenia są szczelne,
              a instalacja może zostać bezpiecznie przekazana do dalszego
              użytkowania albo kolejnego etapu formalnego.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd kominiarski",
    items: [
      {
        id: 291,
        question: "Na czym polega przegląd kominiarski?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd kominiarski polega na ocenie stanu technicznego
              przewodów dymowych, spalinowych i wentylacyjnych.
            </p>
            <p>
              W czasie kontroli sprawdza się drożność przewodów, siłę ciągu,
              stan kanałów, podłączenie urządzeń grzewczych i warunki
              wentylacji, tak aby potwierdzić, czy budynek może być dalej
              bezpiecznie użytkowany.
            </p>
          </>
        ),
      },
      {
        id: 292,
        question: "Czy przegląd kominiarski jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Okresowa kontrola przewodów kominowych jest obowiązkowa co
              najmniej raz w roku.
            </p>
            <p>
              Wynika to z Prawa budowlanego i dotyczy przewodów dymowych,
              spalinowych oraz wentylacyjnych. Obowiązek spoczywa na
              właścicielu albo zarządcy budynku.
            </p>
          </>
        ),
      },
      {
        id: 293,
        question: "Co ile należy wykonywać przegląd kominiarski?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd kominiarski wykonuje się co najmniej raz w roku.
            </p>
            <p>
              Trzeba przy tym pamiętać, że roczna kontrola nie zastępuje
              obowiązkowego czyszczenia przewodów, które wykonuje się z inną
              częstotliwością zależną od rodzaju paliwa i sposobu użytkowania
              instalacji.
            </p>
          </>
        ),
      },
      {
        id: 294,
        question: "Kto wykonuje przegląd kominiarski?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd kominiarski powinien wykonywać mistrz kominiarski albo
              inna osoba posiadająca właściwe uprawnienia w zakresie takiej
              kontroli.
            </p>
            <p>
              Dla właściciela najważniejsze jest to, aby kontrolę prowadził
              specjalista, który potrafi ocenić stan przewodów, wentylacji i
              sporządzić ważny protokół po wizycie.
            </p>
          </>
        ),
      },
      {
        id: 295,
        question:
          "Czy przegląd kominiarski wykonuje się w domu jednorodzinnym i w bloku?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Przegląd kominiarski wykonuje się zarówno w domu
              jednorodzinnym, jak i w budynku wielorodzinnym oraz w mieszkaniach
              korzystających z przewodów kominowych i wentylacyjnych.
            </p>
            <p>
              Różni się jedynie zakres obiektu i organizacja wizyty, ale sam cel
              kontroli pozostaje ten sam: potwierdzenie, że przewody działają
              bezpiecznie.
            </p>
          </>
        ),
      },
      {
        id: 296,
        question: "Ile kosztuje przegląd kominiarski?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od rodzaju budynku, liczby przewodów, dostępu do
              kominów i zakresu samej kontroli.
            </p>
            <p>
              Inaczej wycenia się prosty dom jednorodzinny, a inaczej kamienicę,
              blok albo obiekt usługowy. Na koszt wpływa też to, czy w zakres
              wchodzi tylko standardowa kontrola, czy również szersza diagnostyka
              i bardziej rozbudowany protokół.
            </p>
          </>
        ),
      },
      {
        id: 297,
        question: "Czy po przeglądzie kominiarskim otrzymuje się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Po przeglądzie właściciel albo zarządca powinien otrzymać
              protokół potwierdzający zakres kontroli i jej wynik.
            </p>
            <p>
              Dokument porządkuje obowiązkową dokumentację budynku i wskazuje,
              czy przewody kominowe i wentylacja nadają się do dalszego
              użytkowania, czy wymagają napraw albo dodatkowych działań.
            </p>
          </>
        ),
      },
      {
        id: 298,
        question:
          "Czy przegląd kominiarski jest wymagany przy piecu gazowym?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Jeśli w budynku pracuje piec lub kocioł gazowy, kontrola
              przewodów spalinowych i wentylacyjnych jest szczególnie ważna.
            </p>
            <p>
              To właśnie od prawidłowego odprowadzania spalin i sprawnej
              wentylacji zależy bezpieczna praca urządzenia oraz ograniczenie
              ryzyka cofania spalin i zatrucia tlenkiem węgla.
            </p>
          </>
        ),
      },
      {
        id: 299,
        question: "Jak długo trwa przegląd kominiarski?",
        answer: (
          <>
            <p className="mb-2">
              Czas wizyty zależy od wielkości budynku, liczby przewodów i
              dostępności miejsc, które trzeba sprawdzić.
            </p>
            <p>
              W prostym domu lub mieszkaniu kontrola zwykle mieści się w jednej
              wizycie, ale w większych budynkach albo przy dodatkowej
              diagnostyce może potrwać wyraźnie dłużej.
            </p>
          </>
        ),
      },
      {
        id: 300,
        question: "Czy przegląd kominiarski obejmuje kontrolę wentylacji?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Kontrola kominiarska obejmuje również przewody wentylacyjne i
              ocenę działania wentylacji w pomieszczeniach.
            </p>
            <p>
              Ma to duże znaczenie zwłaszcza tam, gdzie pracują urządzenia
              gazowe, kominki albo inne źródła spalania, bo zaburzona wentylacja
              bezpośrednio wpływa na bezpieczeństwo użytkowania budynku.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd klimatyzacji",
    items: [
      {
        id: 301,
        question: "Czy przegląd klimatyzacji jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Dla typowej klimatyzacji w mieszkaniu albo domu nie ma jednego
              ogólnego obowiązkowego corocznego przeglądu na wzór instalacji
              gazowej czy kominów.
            </p>
            <p>
              Regularny serwis jest jednak standardem technicznym, często wynika
              z warunków gwarancji, a przy części urządzeń i instalacji mogą
              dochodzić dodatkowe obowiązki eksploatacyjne związane z
              urządzeniami zawierającymi f-gazy i zakresem prac wykonywanych
              przez certyfikowany personel.
            </p>
          </>
        ),
      },
      {
        id: 302,
        question: "Jak często należy wykonywać przegląd klimatyzacji?",
        answer: (
          <>
            <p className="mb-2">
              Najbezpieczniej wykonywać przegląd klimatyzacji co najmniej raz w
              roku.
            </p>
            <p>
              Jeśli urządzenie pracuje intensywnie przez większą część roku, na
              przykład w biurze, lokalu usługowym albo mocno nasłonecznionym
              mieszkaniu, warto robić serwis dwa razy w roku: przed sezonem i po
              okresie największego obciążenia.
            </p>
          </>
        ),
      },
      {
        id: 303,
        question: "Ile kosztuje przegląd klimatyzacji?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od typu urządzenia, liczby jednostek i zakresu
              czyszczenia oraz diagnostyki.
            </p>
            <p>
              Inaczej wycenia się pojedynczy klimatyzator typu split w
              mieszkaniu, a inaczej kilka jednostek w domu, biurze albo lokalu
              usługowym. Na koszt wpływa też to, czy w zakres usługi wchodzi
              dezynfekcja i dokładniejsze czyszczenie urządzenia.
            </p>
          </>
        ),
      },
      {
        id: 304,
        question:
          "Czy przegląd klimatyzacji obejmuje czyszczenie urządzenia?",
        answer: (
          <>
            <p className="mb-2">
              Tak, typowy przegląd klimatyzacji obejmuje podstawowe czyszczenie
              urządzenia.
            </p>
            <p>
              Najczęściej dotyczy to filtrów, parownika, obudowy i odpływu
              skroplin. Warto jednak sprawdzić zakres oferty, bo głębsze
              czyszczenie chemiczne albo rozszerzona dezynfekcja mogą być
              rozliczane jako osobna część usługi.
            </p>
          </>
        ),
      },
      {
        id: 305,
        question:
          "Czy klimatyzację trzeba serwisować przed sezonem letnim?",
        answer: (
          <>
            <p className="mb-2">
              Tak, to najlepszy moment na wykonanie standardowego przeglądu.
            </p>
            <p>
              Dzięki temu urządzenie wchodzi w sezon po czyszczeniu, z
              drożnym odpływem skroplin i bez ukrytych usterek, które mogłyby
              wyjść dopiero przy największych upałach.
            </p>
          </>
        ),
      },
      {
        id: 306,
        question: "Jak długo trwa serwis klimatyzacji?",
        answer: (
          <>
            <p className="mb-2">
              Czas serwisu zależy od typu urządzenia, jego stanu i liczby
              jednostek.
            </p>
            <p>
              Przy jednej standardowej jednostce w mieszkaniu wizyta zwykle
              mieści się w jednej godzinie do dwóch godzin, ale mocno zabrudzone
              urządzenie albo większy układ mogą wymagać dłuższego serwisu.
            </p>
          </>
        ),
      },
      {
        id: 307,
        question:
          "Czy klimatyzacja w mieszkaniu również wymaga przeglądu?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Klimatyzacja w mieszkaniu też powinna być regularnie
              serwisowana.
            </p>
            <p>
              Nawet jeśli urządzenie działa tylko sezonowo, w środku gromadzą
              się kurz, wilgoć i zanieczyszczenia, które wpływają na komfort,
              zapach i wydajność pracy całego układu.
            </p>
          </>
        ),
      },
      {
        id: 308,
        question: "Czy klimatyzacja może powodować nieprzyjemny zapach?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Nieprzyjemny zapach z klimatyzacji to jeden z najczęstszych
              sygnałów, że urządzenie wymaga serwisu.
            </p>
            <p>
              Przyczyną zwykle są zabrudzone filtry, wilgoć w jednostce,
              zanieczyszczony parownik albo problem z odpływem skroplin. Właśnie
              dlatego regularne czyszczenie i dezynfekcja mają tak duże
              znaczenie praktyczne.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd pompy ciepła",
    items: [
      {
        id: 311,
        question: "Czy przegląd pompy ciepła jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Dla typowej pompy ciepła w domu jednorodzinnym nie ma jednego
              ogólnego obowiązkowego corocznego przeglądu w takim sensie, jak
              przy instalacji gazowej czy przewodach kominowych.
            </p>
            <p>
              Regularny serwis jest jednak standardem technicznym, często wynika
              z warunków gwarancji producenta, a przy części urządzeń
              zawierających fluorowane gazy cieplarniane mogą dochodzić
              dodatkowe obowiązki eksploatacyjne i kontrolne wynikające z
              przepisów o f-gazach.
            </p>
          </>
        ),
      },
      {
        id: 312,
        question: "Jak często należy wykonywać serwis pompy ciepła?",
        answer: (
          <>
            <p className="mb-2">
              Najczęściej zaleca się wykonywanie serwisu pompy ciepła raz w
              roku.
            </p>
            <p>
              To podejście odpowiada praktyce producentów i pozwala sprawdzić
              urządzenie przed kolejnym sezonem intensywnej pracy. Przy
              nietypowych objawach albo rozbudowanych układach serwis może być
              potrzebny wcześniej.
            </p>
          </>
        ),
      },
      {
        id: 313,
        question: "Ile kosztuje przegląd pompy ciepła?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od typu pompy ciepła, zakresu serwisu i stopnia
              rozbudowania całej instalacji.
            </p>
            <p>
              Inaczej wycenia się prosty układ w domu jednorodzinnym, a inaczej
              system z buforem, zasobnikiem ciepłej wody, kilkoma obiegami i
              szerszą automatyką. Na koszt wpływa też to, czy chodzi o zwykły
              serwis okresowy, czy wizytę diagnostyczną po problemach z pracą
              urządzenia.
            </p>
          </>
        ),
      },
      {
        id: 314,
        question:
          "Czy przegląd pompy ciepła jest wymagany do utrzymania gwarancji?",
        answer: (
          <>
            <p className="mb-2">
              Bardzo często tak. Wielu producentów wiąże utrzymanie gwarancji z
              regularnym serwisem wykonywanym zgodnie z warunkami gwarancyjnymi.
            </p>
            <p>
              Dlatego przed pominięciem przeglądu warto sprawdzić dokumentację
              konkretnego urządzenia. Brak potwierdzonego serwisu może później
              utrudnić dochodzenie roszczeń gwarancyjnych.
            </p>
          </>
        ),
      },
      {
        id: 315,
        question: "Jak długo trwa serwis pompy ciepła?",
        answer: (
          <>
            <p className="mb-2">
              Czas serwisu zależy od typu urządzenia, zakresu kontroli i stopnia
              rozbudowania całej instalacji.
            </p>
            <p>
              Standardowa wizyta serwisowa zwykle mieści się w jednej wizycie,
              ale przy bardziej złożonych układach albo dodatkowej diagnostyce
              może potrwać wyraźnie dłużej.
            </p>
          </>
        ),
      },
      {
        id: 316,
        question: "Czy pompę ciepła można serwisować samodzielnie?",
        answer: (
          <>
            <p className="mb-2">
              Użytkownik może wykonywać tylko podstawowe czynności wskazane przez
              producenta, takie jak kontrola ustawień, czystości otoczenia
              urządzenia czy prostych filtrów, jeśli instrukcja to dopuszcza.
            </p>
            <p>
              Właściwy serwis pompy ciepła powinien prowadzić specjalista, a
              prace przy obiegu chłodniczym mogą wymagać odpowiednich
              kwalifikacji i certyfikatów. Samodzielna ingerencja może pogorszyć
              stan urządzenia i naruszyć warunki gwarancji.
            </p>
          </>
        ),
      },
      {
        id: 317,
        question: "Czy pompa ciepła wymaga corocznego przeglądu?",
        answer: (
          <>
            <p className="mb-2">
              W praktyce tak właśnie należy ją traktować: coroczny przegląd to
              najbezpieczniejszy i najczęściej rekomendowany standard
              eksploatacyjny.
            </p>
            <p>
              Nawet jeśli dla konkretnego domowego urządzenia nie wynika z tego
              jeden uniwersalny obowiązek ustawowy, regularny coroczny serwis
              pomaga utrzymać sprawność, ogranicza ryzyko awarii i porządkuje
              historię urządzenia.
            </p>
          </>
        ),
      },
      {
        id: 318,
        question:
          "Czy pompa ciepła współpracująca z fotowoltaiką wymaga dodatkowej kontroli?",
        answer: (
          <>
            <p className="mb-2">
              Sama pompa ciepła nie wymaga przez to automatycznie zupełnie
              odrębnego serwisu, ale warto sprawdzać cały układ jako system
              współpracujących urządzeń.
            </p>
            <p>
              W praktyce liczy się nie tylko stan pompy ciepła, ale też poprawne
              ustawienia automatyki, sposób współpracy z instalacją
              fotowoltaiczną, magazynem energii lub innymi elementami systemu.
              To pozwala lepiej ocenić realną wydajność i ekonomię pracy całego
              układu.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd wentylacji",
    items: [
      {
        id: 321,
        question: "Czy przegląd wentylacji jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Obowiązek zależy od rodzaju instalacji i zakresu kontroli, o
              której mówimy.
            </p>
            <p>
              W budynkach przewody wentylacyjne objęte kontrolą stanu
              technicznego przewodów kominowych podlegają okresowym kontrolom
              wynikającym z przepisów prawa budowlanego. W przypadku wentylacji
              mechanicznej i rekuperacji nie działa to w identyczny sposób dla
              każdej domowej instalacji, ale regularny serwis pozostaje
              praktycznie konieczny, bo wpływa na bezpieczeństwo, higienę i
              sprawność całego układu.
            </p>
          </>
        ),
      },
      {
        id: 322,
        question: "Jak często należy wykonywać przegląd wentylacji?",
        answer: (
          <>
            <p className="mb-2">
              Dla przewodów wentylacyjnych objętych okresem kontroli budynku
              punktem odniesienia jest zwykle kontrola roczna, a dla wentylacji
              mechanicznej i rekuperacji najczęściej przyjmuje się regularny
              serwis co najmniej raz w roku.
            </p>
            <p>
              W praktyce przy rekuperacji część czynności wykonuje się częściej,
              zwłaszcza wymianę lub czyszczenie filtrów, zwykle co kilka
              miesięcy. Jeśli instalacja pracuje intensywnie albo pojawiają się
              objawy pogorszenia działania, serwis warto zlecić wcześniej.
            </p>
          </>
        ),
      },
      {
        id: 323,
        question: "Ile kosztuje przegląd wentylacji mechanicznej?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od wielkości instalacji, typu centrali, liczby punktów
              nawiewnych i wywiewnych oraz zakresu prac.
            </p>
            <p>
              Inaczej wycenia się podstawową wizytę serwisową w domu
              jednorodzinnym, a inaczej przegląd obejmujący pomiary przepływu,
              regulację, wymianę filtrów i dodatkowe czyszczenie elementów
              instalacji. Dlatego przy porównywaniu ofert warto sprawdzić, co
              dokładnie wchodzi w cenę.
            </p>
          </>
        ),
      },
      {
        id: 324,
        question: "Czy rekuperacja wymaga regularnego serwisu?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Rekuperacja wymaga regularnego serwisu, jeśli ma działać
              sprawnie i higienicznie.
            </p>
            <p>
              W praktyce oznacza to przede wszystkim kontrolę filtrów,
              wymiennika, wentylatorów, odpływu skroplin, automatyki oraz
              bilansu nawiewu i wywiewu. Zaniedbanie tych elementów szybko
              przekłada się na spadek jakości powietrza, większe zużycie energii
              i gorszą pracę całej instalacji.
            </p>
          </>
        ),
      },
      {
        id: 325,
        question: "Czy przegląd wentylacji obejmuje czyszczenie kanałów?",
        answer: (
          <>
            <p className="mb-2">
              Nie zawsze. Standardowy przegląd wentylacji nie musi automatycznie
              obejmować pełnego czyszczenia kanałów.
            </p>
            <p>
              Typowa wizyta zwykle pozwala ocenić stan instalacji, wyczyścić lub
              wymienić filtry i sprawdzić elementy najbardziej narażone na
              zabrudzenie. Głębsze czyszczenie kanałów wykonuje się wtedy, gdy
              stan instalacji, warunki użytkowania albo wyniki kontroli pokazują
              realną potrzebę takich prac.
            </p>
          </>
        ),
      },
      {
        id: 326,
        question: "Jak długo trwa serwis wentylacji?",
        answer: (
          <>
            <p className="mb-2">
              Czas serwisu zależy od rodzaju instalacji i zakresu prac, ale
              zwykle mieści się w jednej wizycie.
            </p>
            <p>
              Prostsza kontrola może potrwać stosunkowo krótko, natomiast
              serwis wentylacji mechanicznej z rekuperacją, pomiarami,
              czyszczeniem centrali i regulacją przepływów może zająć wyraźnie
              więcej czasu. Najwięcej zależy od wielkości domu i stanu samej
              instalacji.
            </p>
          </>
        ),
      },
      {
        id: 327,
        question:
          "Czy wentylację w domu jednorodzinnym trzeba kontrolować?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Wentylację w domu jednorodzinnym również warto regularnie
              kontrolować.
            </p>
            <p>
              Dotyczy to zarówno przewodów wentylacyjnych, jak i wentylacji
              mechanicznej czy rekuperacji. W domu to właśnie właściciel
              najszybciej odczuwa skutki zaniedbań: podwyższoną wilgotność,
              zapachy, zaparowane okna, gorszą jakość powietrza i spadek komfortu
              codziennego użytkowania pomieszczeń.
            </p>
          </>
        ),
      },
      {
        id: 328,
        question: "Czy przegląd wentylacji wykonuje kominiarz?",
        answer: (
          <>
            <p className="mb-2">
              To zależy od rodzaju instalacji. Kominiarz wykonuje kontrole
              przewodów kominowych, w tym przewodów wentylacyjnych w układach,
              które podlegają takiej kontroli.
            </p>
            <p>
              Serwis wentylacji mechanicznej i rekuperacji zwykle wykonuje
              specjalista od instalacji HVAC lub serwisant danej centrali. W
              praktyce trzeba więc odróżnić kontrolę przewodów wentylacyjnych od
              serwisu mechanicznego systemu nawiewno-wywiewnego.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd systemów wykrywania pożaru",
    items: [
      {
        id: 331,
        question: "Na czym polega przegląd systemów wykrywania pożaru?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd polega na sprawdzeniu, czy system wykrywa zagrożenie,
              poprawnie uruchamia alarm i zachowuje sprawność poszczególnych
              elementów.
            </p>
            <p>
              W praktyce kontrola obejmuje centrale, czujki, sygnalizatory,
              zasilanie i podstawową dokumentację eksploatacyjną, a jej celem
              jest potwierdzenie, że system zadziała wtedy, gdy będzie
              rzeczywiście potrzebny.
            </p>
          </>
        ),
      },
      {
        id: 332,
        question:
          "Czy przegląd systemów sygnalizacji pożaru jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Tak, w obiektach wyposażonych w taki system jego przeglądy i
              konserwacja nie są traktowane jako temat opcjonalny.
            </p>
            <p>
              Zakres i częstotliwość wynikają z dokumentacji systemu,
              przyjętych zasad eksploatacji oraz wymagań dla urządzeń
              przeciwpożarowych. Dla zarządcy albo właściciela obiektu chodzi
              nie tylko o formalność, ale o utrzymanie realnej sprawności
              ochrony pożarowej.
            </p>
          </>
        ),
      },
      {
        id: 333,
        question:
          "Jak często należy wykonywać przegląd systemów wykrywania pożaru?",
        answer: (
          <>
            <p className="mb-2">
              Częstotliwość zależy od rodzaju systemu, producenta i charakteru
              obiektu, ale przy systemach sygnalizacji pożaru regularne
              przeglądy i konserwacja są konieczne.
            </p>
            <p>
              W praktyce stosuje się harmonogramy obejmujące zarówno częstsze
              czynności konserwacyjne, jak i bardziej kompleksowe przeglądy
              okresowe. Nie warto odkładać kontroli do momentu pojawienia się
              awarii lub fałszywych alarmów.
            </p>
          </>
        ),
      },
      {
        id: 334,
        question:
          "Czy przegląd obejmuje czujniki dymu i czujniki ciepła?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Czujniki dymu i czujniki ciepła należą do podstawowych
              elementów systemów wykrywania pożaru i są objęte przeglądem.
            </p>
            <p>
              Podczas kontroli sprawdza się ich stan, reakcję na test,
              zabrudzenie, sposób montażu oraz to, czy są właściwie dobrane do
              warunków panujących w danej części obiektu.
            </p>
          </>
        ),
      },
      {
        id: 335,
        question:
          "Kto może wykonywać przegląd systemów sygnalizacji pożaru?",
        answer: (
          <>
            <p className="mb-2">
              Przeglądy powinny wykonywać firmy i technicy mający właściwe
              przygotowanie do obsługi danego systemu oraz doświadczenie w
              urządzeniach przeciwpożarowych.
            </p>
            <p>
              W praktyce liczy się znajomość konkretnej centrali, zasad
              konserwacji i prowadzenia dokumentacji serwisowej. Przy systemach
              pożarowych nie wystarcza przypadkowa kontrola bez odpowiednich
              kwalifikacji i zaplecza technicznego.
            </p>
          </>
        ),
      },
      {
        id: 336,
        question: "Ile kosztuje przegląd systemów wykrywania pożaru?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od wielkości obiektu, liczby elementów systemu,
              rodzaju centrali i zakresu czynności przewidzianych do wykonania.
            </p>
            <p>
              Inaczej wycenia się prostszy układ z niewielką liczbą punktów
              detekcji, a inaczej pełny system sygnalizacji pożaru z wieloma
              strefami i dodatkowymi integracjami. Dlatego w praktyce warto
              porównywać nie tylko cenę, ale dokładny zakres usługi.
            </p>
          </>
        ),
      },
      {
        id: 337,
        question:
          "Czy po przeglądzie systemu przeciwpożarowego otrzymuje się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Tak, po przeglądzie powinien zostać przygotowany protokół albo
              inna dokumentacja potwierdzająca wykonanie czynności serwisowych i
              stan systemu.
            </p>
            <p>
              Taki dokument porządkuje eksploatację obiektu, pokazuje wykryte
              usterki i stanowi podstawę do planowania dalszych napraw lub
              konserwacji. Bez dokumentacji trudniej wykazać, że system był
              utrzymywany we właściwym stanie.
            </p>
          </>
        ),
      },
      {
        id: 338,
        question:
          "Jak długo trwa kontrola systemów wykrywania pożaru?",
        answer: (
          <>
            <p className="mb-2">
              Czas trwania zależy od skali instalacji i liczby elementów, które
              trzeba sprawdzić.
            </p>
            <p>
              Prostsza kontrola niewielkiego układu może zamknąć się w jednej
              krótszej wizycie, ale w większych obiektach przegląd trwa dłużej,
              bo obejmuje wiele stref, urządzeń i testów funkcjonalnych.
            </p>
          </>
        ),
      },
      {
        id: 339,
        question:
          "Czy systemy wykrywania pożaru trzeba regularnie konserwować?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Regularna konserwacja jest konieczna, jeśli system ma
              zachować sprawność między kolejnymi przeglądami.
            </p>
            <p>
              To właśnie konserwacja pozwala wychwycić zabrudzenia, zużycie
              akumulatorów, niestabilną pracę czujek i inne problemy, które
              jeszcze nie muszą dawać jawnej awarii, ale już obniżają
              wiarygodność całego układu.
            </p>
          </>
        ),
      },
      {
        id: 340,
        question:
          "Czy przegląd systemów pożarowych obejmuje test alarmu?",
        answer: (
          <>
            <p className="mb-2">
              Tak, test alarmu jest jednym z ważnych elementów kontroli,
              oczywiście w zakresie dopasowanym do rodzaju systemu i zasad
              obowiązujących w obiekcie.
            </p>
            <p>
              Celem jest potwierdzenie, że system nie tylko wygląda poprawnie w
              dokumentacji, ale potrafi również realnie zareagować, przekazać
              sygnał alarmowy i uruchomić właściwą sekwencję działania.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd sprzętu przeciwpożarowego",
    items: [
      {
        id: 341,
        question: "Jak często należy wykonywać przegląd gaśnic?",
        answer: (
          <>
            <p className="mb-2">
              Gaśnice powinny być objęte przeglądami technicznymi i
              czynnościami konserwacyjnymi zgodnie z zasadami określonymi przez
              producenta, nie rzadziej niż raz w roku.
            </p>
            <p>
              W praktyce oznacza to regularny, coroczny serwis, niezależnie od
              tego, czy urządzenie wygląda na nieużywane. Tylko taka kontrola
              pozwala potwierdzić, że gaśnica jest gotowa do działania.
            </p>
          </>
        ),
      },
      {
        id: 342,
        question:
          "Czy przegląd sprzętu przeciwpożarowego jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Sprzęt i urządzenia przeciwpożarowe w obiekcie powinny być
              regularnie kontrolowane i konserwowane.
            </p>
            <p>
              Dla właściciela albo zarządcy nie jest to temat dodatkowy, lecz
              element prawidłowego utrzymania budynku. Liczy się zarówno sama
              sprawność urządzeń, jak i dokumentacja potwierdzająca wykonanie
              przeglądów.
            </p>
          </>
        ),
      },
      {
        id: 343,
        question:
          "Kto może wykonywać przegląd gaśnic i hydrantów?",
        answer: (
          <>
            <p className="mb-2">
              Przeglądy powinny wykonywać wyspecjalizowane podmioty serwisowe
              albo technicy działający zgodnie z wymaganiami producenta,
              dokumentacją urządzeń i zasadami stosowanymi dla sprzętu
              przeciwpożarowego.
            </p>
            <p>
              W praktyce liczy się doświadczenie, odpowiednie zaplecze
              techniczne i poprawne prowadzenie dokumentacji serwisowej. Przy
              sprzęcie PPOŻ nie warto opierać się na przypadkowej kontroli bez
              potwierdzonej praktyki w tym zakresie.
            </p>
          </>
        ),
      },
      {
        id: 344,
        question: "Ile kosztuje przegląd sprzętu PPOŻ?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od liczby urządzeń, rodzaju obiektu i zakresu
              czynności przewidzianych do wykonania.
            </p>
            <p>
              Inaczej wycenia się pojedyncze gaśnice w małym lokalu, a inaczej
              pakiet obejmujący gaśnice, hydranty, dokumentację i większą liczbę
              punktów w budynku. Dlatego najlepiej porównywać oferty wraz z
              dokładnym opisem zakresu usługi.
            </p>
          </>
        ),
      },
      {
        id: 345,
        question: "Czy po przeglądzie gaśnicy otrzymuje się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Tak, po wykonaniu przeglądu powinno zostać potwierdzone, że
              czynności serwisowe zostały wykonane i jaki jest stan urządzenia.
            </p>
            <p>
              W zależności od zakresu usługi może to być protokół, wpis do
              dokumentacji albo oznaczenie serwisowe urządzenia. Najważniejsze
              jest to, aby właściciel lub zarządca miał czytelne potwierdzenie
              wykonania kontroli.
            </p>
          </>
        ),
      },
      {
        id: 346,
        question: "Jak długo trwa przegląd hydrantów?",
        answer: (
          <>
            <p className="mb-2">
              Czas trwania zależy od liczby hydrantów, układu obiektu i zakresu
              wykonywanych prób.
            </p>
            <p>
              Przy pojedynczych punktach kontrola może przebiegać stosunkowo
              sprawnie, ale w większych budynkach z wieloma urządzeniami i
              dodatkowymi pomiarami przegląd trwa wyraźnie dłużej. W praktyce
              najwięcej zależy od skali instalacji i dostępności urządzeń.
            </p>
          </>
        ),
      },
      {
        id: 347,
        question:
          "Czy gaśnice w budynkach muszą być regularnie serwisowane?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Gaśnice powinny być regularnie serwisowane, bo tylko wtedy
              można potwierdzić ich gotowość do użycia.
            </p>
            <p>
              W praktyce brak regularnego serwisu oznacza ryzyko, że urządzenie
              będzie niesprawne, nieaktualne albo źle dobrane do warunków
              panujących w obiekcie. Dla zarządcy to także problem
              dokumentacyjny przy kontroli budynku.
            </p>
          </>
        ),
      },
      {
        id: 348,
        question:
          "Czy hydranty wewnętrzne wymagają okresowej kontroli?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Hydranty wewnętrzne wymagają okresowej kontroli, bo są częścią
              wyposażenia bezpieczeństwa pożarowego budynku.
            </p>
            <p>
              Kontrola ma potwierdzić nie tylko kompletność urządzenia, ale też
              jego dostępność, szczelność i gotowość do uruchomienia. Sam fakt,
              że hydrant jest zamontowany, nie oznacza jeszcze, że spełni swoją
              funkcję podczas realnego zdarzenia.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd zabezpieczeń ewakuacyjnych i przeciwpożarowych",
    items: [
      {
        id: 351,
        question:
          "Czy przegląd zabezpieczeń przeciwpożarowych jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Tak, zabezpieczenia przeciwpożarowe stosowane w obiekcie powinny
              być utrzymywane w stanie sprawności technicznej i funkcjonalnej
              oraz objęte regularnymi przeglądami i konserwacją.
            </p>
            <p>
              W praktyce obowiązek nie dotyczy tylko samego montażu urządzeń,
              ale także ich dalszego utrzymania. Właściciel albo zarządca
              budynku odpowiada za to, aby zabezpieczenia były gotowe do
              działania przez cały okres użytkowania obiektu.
            </p>
          </>
        ),
      },
      {
        id: 352,
        question:
          "Jak często należy wykonywać kontrolę zabezpieczeń ewakuacyjnych?",
        answer: (
          <>
            <p className="mb-2">
              Częstotliwość zależy od rodzaju urządzenia, dokumentacji producenta
              i zasad przyjętych dla danego systemu, ale punktem odniesienia dla
              urządzeń przeciwpożarowych pozostaje regularna kontrola nie
              rzadziej niż raz w roku.
            </p>
            <p>
              W praktyce część elementów może wymagać częstszych testów albo
              dodatkowych czynności konserwacyjnych. Dlatego przy obiektach z
              rozbudowanymi systemami warto patrzeć nie tylko na termin roczny,
              ale na cały harmonogram obsługi.
            </p>
          </>
        ),
      },
      {
        id: 353,
        question:
          "Czy oświetlenie ewakuacyjne wymaga regularnych przeglądów?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Oświetlenie ewakuacyjne powinno być regularnie kontrolowane,
              bo jego rola ujawnia się właśnie wtedy, gdy zanika podstawowe
              zasilanie.
            </p>
            <p>
              W czasie przeglądów potwierdza się, czy oprawy uruchamiają się
              prawidłowo, czytelnie wskazują drogę ewakuacji i zachowują czas
              działania potrzebny do bezpiecznego opuszczenia obiektu.
            </p>
          </>
        ),
      },
      {
        id: 354,
        question:
          "Czy drzwi przeciwpożarowe muszą być kontrolowane?",
        answer: (
          <>
            <p className="mb-2">
              Tak. Drzwi przeciwpożarowe również wymagają kontroli, ponieważ
              mają zachować funkcję pożarową i wspierać bezpieczną ewakuację.
            </p>
            <p>
              W praktyce sprawdza się ich domykanie, stan okuć, zawiasów,
              uszczelek i innych elementów, od których zależy ograniczenie
              rozprzestrzeniania się dymu i ognia. Nawet drobne uszkodzenia mogą
              istotnie obniżyć skuteczność takich drzwi.
            </p>
          </>
        ),
      },
      {
        id: 355,
        question:
          "Kto wykonuje przeglądy systemów przeciwpożarowych w budynkach?",
        answer: (
          <>
            <p className="mb-2">
              Przeglądy wykonują wyspecjalizowane podmioty serwisowe i technicy
              mający przygotowanie odpowiednie do danego rodzaju urządzeń oraz
              dokumentacji systemu.
            </p>
            <p>
              W praktyce liczy się znajomość konkretnych rozwiązań,
              prawidłowego testowania, konserwacji i prowadzenia dokumentacji.
              Dla zarządcy najważniejsze jest to, aby kontrola była prowadzona
              przez wykonawcę, który potrafi realnie ocenić stan zabezpieczeń,
              a nie tylko wykonać pobieżne oględziny.
            </p>
          </>
        ),
      },
      {
        id: 356,
        question:
          "Czy po przeglądzie zabezpieczeń PPOŻ wystawia się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Tak, po przeglądzie powinno zostać przygotowane potwierdzenie
              wykonania kontroli i oceny stanu urządzeń.
            </p>
            <p>
              Może to przybrać formę protokołu, wpisu serwisowego albo innej
              dokumentacji eksploatacyjnej zależnej od rodzaju systemu. Dla
              właściciela i zarządcy kluczowe jest to, aby mieć czytelny ślad po
              przeglądzie oraz listę usterek i zaleceń do wykonania.
            </p>
          </>
        ),
      },
      {
        id: 357,
        question:
          "Jak długo trwa kontrola zabezpieczeń ewakuacyjnych?",
        answer: (
          <>
            <p className="mb-2">
              Czas trwania zależy od wielkości obiektu, liczby urządzeń i zakresu
              testów przewidzianych do wykonania.
            </p>
            <p>
              W mniejszych budynkach kontrola może zamknąć się w jednej krótszej
              wizycie, ale w obiektach z rozbudowanymi systemami, wieloma
              drogami ewakuacyjnymi i dodatkowymi urządzeniami trwa dłużej. W
              praktyce decyduje skala i złożoność zabezpieczeń.
            </p>
          </>
        ),
      },
      {
        id: 358,
        question:
          "Ile kosztuje przegląd systemów przeciwpożarowych?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od rodzaju urządzeń, liczby punktów kontrolnych,
              wielkości budynku i zakresu czynności objętych usługą.
            </p>
            <p>
              Inaczej wycenia się prostą kontrolę wybranych zabezpieczeń, a
              inaczej pełny przegląd kilku systemów wraz z dokumentacją i testami
              funkcjonalnymi. Dlatego przy porównywaniu ofert najlepiej oceniać
              nie tylko kwotę, ale też dokładny zakres przeglądu.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "FAQ - przegląd budowlany",
    items: [
      {
        id: 361,
        question: "Czy przegląd budowlany jest obowiązkowy?",
        answer: (
          <>
            <p className="mb-2">
              Tak, kontrole stanu technicznego obiektów budowlanych wynikają z
              przepisów prawa budowlanego.
            </p>
            <p>
              Zakres i częstotliwość zależą od rodzaju obiektu oraz typu
              kontroli. Dla właściciela albo zarządcy oznacza to obowiązek
              pilnowania terminów i dokumentacji, a nie tylko reagowania na
              usterki wtedy, gdy są już widoczne lub kosztowne.
            </p>
          </>
        ),
      },
      {
        id: 362,
        question:
          "Jak często należy wykonywać przegląd techniczny budynku?",
        answer: (
          <>
            <p className="mb-2">
              W praktyce podstawowe okresy kontroli to przegląd roczny oraz
              przegląd pięcioletni.
            </p>
            <p>
              Kontrola roczna obejmuje określone elementy narażone na wpływy
              atmosferyczne i eksploatację, a kontrola pięcioletnia ma szerszy
              charakter i obejmuje także ocenę stanu technicznego i użytkowego
              całego obiektu. Dokładny obowiązek zależy od rodzaju budynku i
              zakresu, którego dotyczy dana kontrola.
            </p>
          </>
        ),
      },
      {
        id: 363,
        question:
          "Czym różni się przegląd roczny od przeglądu pięcioletniego?",
        answer: (
          <>
            <p className="mb-2">
              Przegląd roczny skupia się przede wszystkim na elementach, które
              najszybciej ulegają zużyciu pod wpływem pogody i codziennej
              eksploatacji.
            </p>
            <p>
              Przegląd pięcioletni ma szerszy zakres i obejmuje ocenę stanu
              technicznego oraz przydatności budynku do użytkowania, a także
              estetykę obiektu i jego otoczenia. To właśnie przy tej kontroli
              wychodzą często problemy trudniejsze do wychwycenia przy ocenie
              bieżącej.
            </p>
          </>
        ),
      },
      {
        id: 364,
        question: "Kto może wykonać przegląd budowlany budynku?",
        answer: (
          <>
            <p className="mb-2">
              Przeglądy wykonują osoby mające odpowiednie uprawnienia budowlane
              albo kwalifikacje właściwe dla danego zakresu kontroli.
            </p>
            <p>
              W praktyce część budowlaną wykonuje specjalista z odpowiednimi
              uprawnieniami, natomiast elementy instalacyjne czy kominowe mogą
              wymagać udziału osób z kwalifikacjami branżowymi. Ważne jest to,
              aby kontrola i dokumentacja były podpisane przez osobę właściwą
              dla danego zakresu.
            </p>
          </>
        ),
      },
      {
        id: 365,
        question: "Ile kosztuje przegląd techniczny budynku?",
        answer: (
          <>
            <p className="mb-2">
              Cena zależy od wielkości budynku, rodzaju obiektu, zakresu
              oględzin i poziomu złożoności kontroli.
            </p>
            <p>
              Inaczej wycenia się dom jednorodzinny, a inaczej budynek
              wielorodzinny lub obiekt usługowy z rozbudowanymi częściami
              wspólnymi. Przy porównywaniu ofert warto sprawdzać, czy cena
              obejmuje tylko wizytę, czy również pełny protokół i zalecenia po
              przeglądzie.
            </p>
          </>
        ),
      },
      {
        id: 366,
        question:
          "Czy przegląd budowlany jest wymagany w domu jednorodzinnym?",
        answer: (
          <>
            <p className="mb-2">
              W domu jednorodzinnym nie wszystkie obowiązki wyglądają identycznie
              jak w budynkach wielorodzinnych czy komercyjnych, ale temat
              kontroli stanu technicznego wcale nie znika.
            </p>
            <p>
              Trzeba odróżnić wyjątki dotyczące części kontroli rocznych od
              obowiązków związanych z innymi elementami budynku i instalacjami.
              W praktyce właściciel domu nadal powinien pilnować kontroli
              wynikających z przepisów oraz stanu technicznego nieruchomości.
            </p>
          </>
        ),
      },
      {
        id: 367,
        question:
          "Czy po przeglądzie budowlanym wystawia się protokół?",
        answer: (
          <>
            <p className="mb-2">
              Tak, po przeglądzie powinna powstać dokumentacja potwierdzająca
              wykonanie kontroli i stan techniczny budynku.
            </p>
            <p>
              Protokół porządkuje obowiązki właściciela lub zarządcy, wskazuje
              wykryte nieprawidłowości i zalecane działania. To dokument ważny
              nie tylko formalnie, ale też praktycznie, bo pozwala planować
              dalsze naprawy i remonty.
            </p>
          </>
        ),
      },
      {
        id: 368,
        question:
          "Jak długo trwa kontrola stanu technicznego budynku?",
        answer: (
          <>
            <p className="mb-2">
              Czas trwania zależy od wielkości obiektu, zakresu kontroli i
              liczby elementów, które trzeba ocenić.
            </p>
            <p>
              W prostszych budynkach kontrola może zamknąć się w jednej krótszej
              wizycie, ale przy większych nieruchomościach albo bardziej
              rozbudowanym zakresie technicznym trwa odpowiednio dłużej. W
              praktyce decyduje skala obiektu i poziom szczegółowości oceny.
            </p>
          </>
        ),
      },
    ],
  },
];

export default FaqSectionsContent;
